/**
 * @unscoped — MailPage 의 i18n — Cycle 54.
 *
 * cycle 50 approval-i18n / cycle 51 calendar-i18n / cycle 52 workspace-i18n 통합 패턴 일관.
 * 4 컴포넌트 (MailPage / ComposeModal / MailDetail / RecipientPicker) 공유.
 *
 * 정책 (cycle 46/49 학습 적용):
 *   - fallback chain: lang → en → FALLBACK_LANG (ko)
 *   - 전수 non-empty 가드는 본 모듈 test 가 강제
 */

import {
  getSharedLang, setSharedLang, subscribeSharedLang,
  FALLBACK_LANG, type Lang,
} from './base-i18n';

/* ════════════════════════════════════════════════════════════════════════════
 * ★★★ `MailI18nKey` 에 ⇒ ⭐ «눈»을 준다 — 2026-10-12 (⇒ i18n 통 ★ 꼴 통일의 ⭐ 첫 통)
 *
 * ## ⭐ 무엇을 바꿨나 ⇒ ★ 코드 ⭐ 두 줄
 *   바로 아래 ⇒ 선언에서 ★ 명시 타입을 ⇒ ⭐ 뗐다
 *   닫는 자리 ⇒ 같은 꼴을 ★ `satisfies` 로 ⇒ ⭐ 다시 걸었다
 * ⇒ ★ 까닭 ⇒ 선언에 `Record<string, …>` 가 붙어 있으면 ⇒ ⭐ `keyof` 가 ★ `string` 이 된다
 *   ⇒ ⇒ ★ 즉 `MailI18nKey` ＝ `string` 이었고 ⇒ ⇒ ⛔ `tMail(아무글자)` 가 ★ 통과했다.
 *
 * ## ⭐⭐ 불변식 — ⛔ 「`satisfies` 글자가 있나」로 재지 말 것 (관제 2026-10-12)
 *   ⇒ ★ 재야 할 것은 ⇒ ⭐ **「사전 선언에 ★ `: Record<string, …>` 애너테이션이 ⭐ 없다」** 하나다.
 *   ⇒ ⇒ ✅ 그 하나가 ★ `satisfies` · `as const satisfies` · ⭐ 「열쇠가 union 인 통」 ★ 셋을 다 통과시킨다.
 *   ⇒ ⇒ ⛔ 그래서 ★ `reservation-i18n` · `app-download-i18n` 의 ⭐ `as const` 를 ★ «떼지 않는다» —
 *      ⇒ ⇒ 그 둘은 ★ 이미 ⭐ 불변식을 넘겼다. ⇒ ★ 통일할 것은 ⭐ «철자»가 아니라 ★ 불변식이다.
 *   ⇒ ⚠ 그리고 ★ 거르개에 ⭐ `[^>]*` 를 쓰지 말 것 — ⇒ ★ 겹친 꺾쇠에서 ⭐ 첫 `>` 에 멈춘다
 *      (⇒ 그 함정이 ★ 2026-10-12 에 ⭐ 「옛 꼴 1건」이라는 ★ 거짓 답을 냈다). ⇒ ★ 꺾쇠 깊이를 ⭐ 세라.
 *
 * ## ⚠ 「0건인데 왜 고치나」 ⇒ ★ 그 0 이 ⭐ «부르는 데가 없다»가 아님을 ★ 쟀다
 *   ```
 *   애너테이션을 떼고 tsc    ⇒ ★ 오류 ⭐ 0 건   (⇒ tsc -p apps/frontend/tsconfig.app.json · 잰 파일 1,156)
 *   ⇒ ⭐ 분모 ⇒ ★ 이 통을 부르는 자리 ⭐ 802  (⇒ 제품 36 파일 · 시험 제외 · 주석 덮고 괄호 맞춰 셈)
 *        리터럴 737 · 삼항 20 · 변수 45 · ⛔ 단언(`as never` 따위) ⭐ 0
 *   ```
 *   ⇒ ✅ 즉 ★ 「이미 깨끗」이다 — ⇒ ⛔ 「부르는 데가 없다」가 ★ 아니다.
 *   ⇒ ⭐⭐ 그리고 ★ `as const` 를 ⇒ ⛔ 붙이지 않았다: ⇒ 목적(`keyof` 가 리터럴 유니온)은
 *      ★ `satisfies` 하나로 서고, ⇒ ⚠ `as const` 는 ★ 값까지 `readonly` 로 만들어 ⇒ ⭐ 받는 쪽에 번진다.
 *      ⇒ ⇒ ★ 이 통은 ⭐ 사전을 «내보내지 않아» ⇒ 지금은 닿을 곳이 0 이지만 ⇒ ★ 통일을 ⭐ 깨지 않는다.
 *
 * ⏳ ★ 만료 조건 ⇒ ⭐ 「`apps/frontend/src/lib/*-i18n.ts` 에 ★ 옛 꼴이 ⭐ 0 이 되는 날 ⇒ 이 절을 지운다」
 *   ⇒ ★ 2026-10-12 기준 ⇒ ⭐ 28 통 중 ★ 옛 꼴 ⭐ 21 · 불변식 통과 ⭐ 7. ⇒ ★ 이 판이 ⭐ 그 첫 하나다.
 *   ⇒ ⚠ 한 통에 ★ 한 판으로 간다(관제 2026-10-12) — ⇒ ⭐ 판이 커지면 ★ 되보기가 눈을 잃는다.
 * ════════════════════════════════════════════════════════════════════════════ */
const MESSAGES = {
  // ----- MailPage 메인 -----
  'mail.sidebar.aria': { ko: '메일 폴더', en: 'Mail folders', ja: 'メールフォルダ' },
  'mail.btn.compose': { ko: '메일 작성', en: 'Compose', ja: 'メール作成' },
  'mail.folder.inbox': { ko: '받은편지함', en: 'Inbox', ja: '受信箱' },
  'mail.folder.star': { ko: '중요', en: 'Starred', ja: '重要' },
  'mail.folder.sent': { ko: '보낸편지함', en: 'Sent', ja: '送信済み' },
  'mail.folder.draft': { ko: '임시보관함', en: 'Drafts', ja: '下書き' },
  'mail.folder.trash': { ko: '휴지통', en: 'Trash', ja: 'ゴミ箱' },
  // 스팸 (migration 111, 2026-08-20) — 스팸으로 보낼 방법이 아예 없었다.
  'mail.folder.spam': { ko: '스팸함', en: 'Spam', ja: '迷惑メール' },
  'mail.bulk.spam': { ko: '스팸', en: 'Spam', ja: '迷惑メール' },
  'mail.bulk.not_spam': { ko: '스팸 아님', en: 'Not spam', ja: '迷惑メールでない' },
  'mail.spam.confirm_title': { ko: '스팸함으로 보낼까요?', en: 'Move to spam?', ja: '迷惑メールに移動しますか？' },
  'mail.spam.confirm_desc': {
    ko: '스팸함으로 옮기고 목록에서 감춥니다. 30일 뒤 자동으로 지워집니다.',
    en: 'Moves to spam and hides it from the list. Deleted automatically after 30 days.',
    ja: '迷惑メールに移動して一覧から隠します。30日後に自動削除されます。',
  },
  'mail.spam.block_sender': {
    ko: '이 사람이 보내는 메일을 앞으로 스팸함으로 보내기',
    en: 'Send future mail from this sender to spam',
    ja: 'この差出人からのメールを今後は迷惑メールへ',
  },
  'mail.spam.confirm_ok': { ko: '스팸으로 보내기', en: 'Move to spam', ja: '迷惑メールへ' },
  'mail.spam.cancel': { ko: '취소', en: 'Cancel', ja: 'キャンセル' },
  // 2026-08-26 — 버튼 이름이 '스팸 아님' → **복원** 으로 바뀌었다. 안내가 없는 버튼을 가리키면
  // 사용자는 그 이름을 찾다가 못 찾는다 — 문구도 같이 옮긴다.
  'mail.spam.banner': {
    ko: '스팸함의 메일은 30일 뒤 자동으로 지워집니다. 잘못 들어온 것이 있으면 복원을 눌러 되돌리세요.',
    en: 'Spam is deleted automatically after 30 days. Use "Restore" to bring back anything misfiled.',
    ja: '迷惑メールは30日後に自動削除されます。誤って入ったものは「元に戻す」で戻せます。',
  },
  'mail.detail.btn_spam_aria': { ko: '스팸으로 신고', en: 'Report spam', ja: '迷惑メールとして報告' },
  'mail.detail.btn_not_spam_aria': { ko: '스팸 아님으로 되돌리기', en: 'Mark as not spam', ja: '迷惑メールでないに戻す' },
  // 검색 (2026-08-20) — 메일 검색은 길 자체가 없었다.
  'mail.search.placeholder': { ko: '메일 검색', en: 'Search mail', ja: 'メール検索' },
  'mail.search.clear': { ko: '검색 지우기', en: 'Clear search', ja: '検索をクリア' },
  'mail.search.empty': {
    ko: '찾는 낱말이 있는 메일이 없습니다.',
    en: 'No mail matches your search.',
    ja: '検索に一致するメールがありません。',
  },
  'mail.list.count_suffix': { ko: '건', en: '', ja: '件' },
  'mail.list.aria_panel_suffix': { ko: '메시지 목록', en: 'message list', ja: 'メッセージ一覧' },
  'mail.list.loading': { ko: '불러오는 중…', en: 'Loading…', ja: '読み込み中...' },
  'mail.list.err_prefix': { ko: '메일 로드 실패', en: 'Mail load failed', ja: 'メール読み込み失敗' },
  'mail.list.empty_title': { ko: '메일이 없습니다', en: 'No mail', ja: 'メールがありません' },
  'mail.list.empty_desc': {
    ko: '현재 폴더에 표시할 메일이 없어요.',
    en: 'No messages to display in this folder.',
    ja: '現在のフォルダに表示するメールがありません。',
  },
  // 목록 정렬 (2026-08-20) — 지금까지는 받은 순서 하나로 고정이었다.
  'mail.sort.aria': { ko: '목록 정렬', en: 'Sort list', ja: '一覧の並び替え' },
  'mail.sort.field_heading': { ko: '기준', en: 'Sort by', ja: '基準' },
  'mail.sort.dir_heading': { ko: '방향', en: 'Order', ja: '方向' },
  'mail.sort.date': { ko: '날짜', en: 'Date', ja: '日付' },
  'mail.sort.sender': { ko: '보낸 사람', en: 'Sender', ja: '差出人' },
  'mail.sort.subject': { ko: '제목', en: 'Subject', ja: '件名' },
  'mail.sort.unread': { ko: '안 읽음 먼저', en: 'Unread first', ja: '未読を先に' },
  'mail.sort.newest': { ko: '최신순', en: 'Newest first', ja: '新しい順' },
  'mail.sort.oldest': { ko: '오래된순', en: 'Oldest first', ja: '古い順' },
  'mail.sort.asc': { ko: '오름차순', en: 'Ascending', ja: '昇順' },
  'mail.sort.desc': { ko: '내림차순', en: 'Descending', ja: '降順' },
  // 여러 개 고르기 (2026-08-20) — 지금까지 상태 변경은 한 통씩만 됐다.
  'mail.bulk.aria_bar': { ko: '고른 메일 처리', en: 'Selected mail actions', ja: '選択したメールの操作' },
  'mail.bulk.aria_pick': { ko: '고르기', en: 'select', ja: '選択' },
  'mail.bulk.picked': { ko: '{n}개 선택', en: '{n} selected', ja: '{n}件選択' },
  'mail.bulk.read': { ko: '읽음', en: 'Mark read', ja: '既読' },
  'mail.bulk.star': { ko: '중요', en: 'Star', ja: '重要' },
  'mail.bulk.trash': { ko: '휴지통', en: 'Trash', ja: 'ゴミ箱' },
  'mail.bulk.clear': { ko: '선택 해제', en: 'Clear', ja: '選択解除' },
  // 휴지통에서 고른 것 (2026-08-25) — 거기서 할 수 있는 일은 되돌리기와 영영 지우기 둘뿐이다.
  'mail.bulk.restore': { ko: '복원', en: 'Restore', ja: '元に戻す' },
  'mail.bulk.purge': { ko: '영구삭제', en: 'Delete forever', ja: '完全に削除' },
  'mail.bulk.purge_confirm_title': { ko: '영원히 지울까요?', en: 'Delete forever?', ja: '完全に削除しますか?' },
  'mail.bulk.purge_confirm_desc': {
    ko: '고른 {n}통을 영원히 지웁니다. 되돌릴 수 없습니다.',
    en: 'The {n} selected mail will be gone for good. This cannot be undone.',
    ja: '選択した{n}件を完全に削除します。元に戻せません。',
  },
  'mail.bulk.restored_title': { ko: '복원했습니다', en: 'Restored', ja: '元に戻しました' },
  'mail.bulk.restored_desc': {
    ko: '{n}통을 받은편지함으로 되돌렸습니다.',
    en: '{n} mail moved back to the inbox.',
    ja: '{n}件を受信トレイに戻しました。',
  },
  'mail.bulk.purged_title': { ko: '지웠습니다', en: 'Deleted', ja: '削除しました' },
  'mail.bulk.purged_desc': {
    ko: '{n}통을 영원히 지웠습니다.',
    en: '{n} mail deleted for good.',
    ja: '{n}件を完全に削除しました。',
  },
  'mail.bulk.notice_ok': { ko: '확인', en: 'OK', ja: 'OK' },
  'mail.item.aria_starred': { ko: '중요 표시됨', en: 'Starred', ja: '重要マーク済み' },
  'mail.item.aria_not_starred': { ko: '중요 표시 없음', en: 'Not starred', ja: '重要マークなし' },
  'mail.item.aria_unread': { ko: '읽지 않음', en: 'Unread', ja: '未読' },
  'mail.item.subject_empty': { ko: '(제목 없음)', en: '(No subject)', ja: '(件名なし)' },
  'mail.item.body_empty': { ko: '(본문 없음)', en: '(No body)', ja: '(本文なし)' },
  // 다음 쪽 (2026-08-20) — 서버는 nextCursor 를 계속 줬는데 화면이 첫 쪽만 받고 끝이었다.
  'mail.list.more': { ko: '더 보기', en: 'Load more', ja: 'もっと見る' },
  'mail.sender.ai_assistant': { ko: 'AI 비서', en: 'AI assistant', ja: 'AIアシスタント' },
  // 외부 메일 가져오기 (migration 112, 2026-08-20) — 사내 메일과 섞이므로 어디서 온 것인지
  // 한눈에 보여야 한다. 답장 경로가 다르기 때문이다(사내는 되고 외부는 아직 안 된다).
  'mail.sender.external_badge': { ko: '외부', en: 'External', ja: '外部' },
  'mail.sender.external_title': {
    ko: '외부 메일 계정으로 받아온 메일입니다',
    en: 'Fetched from an external mail account',
    ja: '外部メールアカウントから取得したメールです',
  },
  // ★ 2026-08-21 migration 113 — 외부 주소로 보내는 길이 생겨 안내 문구가 바뀌었다.
  'mail.detail.external_reply_hint': {
    ko: '답장은 등록한 외부 메일 계정으로 나갑니다',
    en: 'Replies are sent from your registered external mail account',
    ja: '返信は登録した外部メールアカウントから送信されます',
  },

  // ----- ComposeModal -----
  /** 이탈 경고 — 공용 문구에 «이 화면만의 사정»을 더한다(임시 저장이 있다는 것). */
  'mail.compose.unsaved_desc': {
    ko: '쓰던 메일이 사라집니다. 남겨 두려면 「임시 저장」을 먼저 누르세요.',
    en: 'Your draft will be lost. Use “Save draft” to keep it.',
    ja: '作成中のメールが失われます。残すには「下書き保存」を押してください。',
  },
  'mail.compose.heading': { ko: '메일 작성', en: 'Compose mail', ja: 'メール作成' },
  'mail.compose.label_subject': { ko: '제목', en: 'Subject', ja: '件名' },
  'mail.compose.placeholder_subject': { ko: '메일 제목', en: 'Mail subject', ja: 'メール件名' },
  'mail.compose.placeholder_body': { ko: '본문을 입력하세요', en: 'Enter body', ja: '本文を入力' },
  'mail.compose.err.no_recipients': {
    ko: '전송할 메일은 최소 1명의 수신자가 필요합니다.',
    en: 'At least one recipient is required to send mail.',
    ja: '送信には少なくとも1名の受信者が必要です。',
  },
  'mail.compose.warn.pending': {
    ko: '아직 수신자로 확정되지 않은 입력이 있습니다. 목록에서 고르지 않으면 전송에서 빠집니다.',
    en: 'Some text has not been confirmed as a recipient. It will be dropped unless you pick from the list.',
    ja: '受信者として確定していない入力があります。リストから選ばないと送信から外れます。',
  },
  'mail.compose.btn.send_need_to': {
    ko: '받는사람을 지정하세요',
    en: 'Add a recipient',
    ja: '受信者を指定してください',
  },
  // {n} 은 호출부에서 치환한다 — tMail 은 파라미터를 받지 않고, 이 모듈의 전수 검사는
  // 세 언어 모두 비어 있지 않기를 요구한다(빈 문자열로 조각내면 그 검사에 걸린다).
  'mail.compose.btn.send_count': {
    ko: '보내기 · 받는사람 {n}명',
    en: 'Send · {n} recipients',
    ja: '送信 · 受信者{n}名',
  },
  'mail.compose.err.no_subject': { ko: '제목을 입력해 주세요.', en: 'Please enter a subject.', ja: '件名を入力してください。' },
  'mail.compose.btn.saving': { ko: '저장 중…', en: 'Saving…', ja: '保存中...' },
  'mail.compose.btn.sending': { ko: '전송 중…', en: 'Sending…', ja: '送信中...' },
  'mail.compose.btn.save_draft': { ko: '임시 저장', en: 'Save draft', ja: '下書き保存' },
  'mail.compose.btn.send': { ko: '보내기', en: 'Send', ja: '送信' },
  'mail.compose.aria_subject': { ko: '제목', en: 'Subject', ja: '件名' },
  'mail.compose.aria_body': { ko: '본문', en: 'Body', ja: '本文' },

  // ----- MailDetail -----
  'mail.detail.quote_prefix': { ko: '--- 원문 ---', en: '--- Original ---', ja: '--- 原文 ---' },
  'mail.detail.from_label': { ko: '보낸 사람:', en: 'From:', ja: '送信者:' },
  'mail.detail.sent_at_label': { ko: '보낸 시각:', en: 'Sent at:', ja: '送信時刻:' },
  'mail.detail.subject_label': { ko: '제목:', en: 'Subject:', ja: '件名:' },
  'mail.detail.empty_title': { ko: '메일을 선택하세요', en: 'Select a mail', ja: 'メールを選択してください' },
  'mail.detail.empty_desc': {
    ko: '좌측 목록에서 메일을 클릭하면 여기에 본문이 표시됩니다.',
    en: 'Click a mail in the left list to view its body here.',
    ja: '左側のリストからメールをクリックすると本文が表示されます。',
  },
  'mail.detail.btn_star_aria': { ko: '중요 표시', en: 'Star', ja: '重要マーク' },
  'mail.detail.btn_unstar_aria': { ko: '중요 표시 해제', en: 'Unstar', ja: '重要マーク解除' },
  'mail.detail.btn_trash_aria': { ko: '휴지통으로 이동', en: 'Move to trash', ja: 'ゴミ箱に移動' },
  'mail.detail.btn_restore_aria': { ko: '휴지통 복원', en: 'Restore from trash', ja: 'ゴミ箱から復元' },

  // ----- RecipientPicker -----
  'mail.recipient.placeholder_search': {
    ko: '이름·이메일·부서 검색',
    en: 'Search name / email / department',
    ja: '名前・メール・部署検索',
  },
  // 받는사람 확정 경고 (2026-08-20) — 목록에서 고르지 않은 글자는 수신자가 되지 않는다.
  // 실제로 이것 때문에 받는사람 0명인 메일이 전송된 적이 있다(mail_messages id=29).
  'mail.recipient.pending_hint': {
    ko: '목록에서 골라야 수신자가 됩니다.',
    en: 'Pick from the list to add a recipient.',
    ja: 'リストから選ぶと受信者になります。',
  },
  'mail.recipient.pending_external': {
    ko: '외부 주소는 아직 보낼 수 없습니다 — 지금은 사내 메일만 가능합니다.',
    en: 'External addresses are not supported yet — internal mail only for now.',
    ja: '外部アドレスはまだ送信できません — 現在は社内メールのみです。',
  },
  'mail.recipient.searching': { ko: '검색 중…', en: 'Searching…', ja: '検索中...' },
  'mail.recipient.aria_selected_suffix': { ko: '선택된 수신자', en: 'selected recipients', ja: '選択された受信者' },
  'mail.recipient.aria_remove_suffix': { ko: '제거', en: 'Remove', ja: '削除' },

  // ----- 보기 설정 · 요약 · 줄 위 작업 (2026-08-21) -----
  'mail.btn.self_compose': { ko: '내게 쓰기', en: 'To myself', ja: '自分宛て' },
  // ★ 넷은 겹쳐 쓰는 거르개가 아니라 **하나씩 열어 보는 조회**다 (2026-08-21 사용자 지시).
  'mail.stat.aria': {
    ko: '빠른 조회 — 하나를 누르면 그것만 봅니다',
    en: 'Quick views — pick one to see just those',
    ja: 'クイック表示 — 一つ選ぶとそれだけを表示',
  },
  'mail.stat.unread': { ko: '안읽음', en: 'Unread', ja: '未読' },
  'mail.stat.star': { ko: '중요', en: 'Starred', ja: '重要' },
  'mail.stat.clip': { ko: '첨부', en: 'Attached', ja: '添付' },
  'mail.stat.to': { ko: '나에게', en: 'To me', ja: '自分宛' },
  'mail.stat.na_title': {
    ko: '서버가 목록에 이 정보를 아직 실어 주지 않습니다',
    en: 'The list API does not return this yet',
    ja: 'サーバーがこの情報をまだ返しません',
  },
  'mail.stat.partial_title': {
    ko: '지금 불러온 목록 기준입니다 — 더 받으면 늘어납니다',
    en: 'Counted from the mail loaded so far',
    ja: '現在読み込んだ分の集計です',
  },
  'mail.view.aria': { ko: '열림 설정', en: 'Open layout', ja: '開き方の設定' },
  'mail.view.heading': { ko: '메일을 어떻게 열까', en: 'How to open mail', ja: 'メールの開き方' },
  'mail.view.full': { ko: '전체열림', en: 'Full', ja: '全画面' },
  'mail.view.full_desc': { ko: '고르면 바로 창 전체로', en: 'Fills the whole pane', ja: '選ぶとすぐ全画面' },
  'mail.view.side': { ko: '좌우열림', en: 'Side by side', ja: '左右' },
  'mail.view.side_desc': { ko: '목록은 왼쪽, 메일은 오른쪽', en: 'List left, mail right', ja: '一覧は左、メールは右' },
  'mail.view.bottom': { ko: '상하열림', en: 'Bottom pane', ja: '上下' },
  'mail.view.bottom_desc': { ko: '메일이 아래에서 올라온다', en: 'Mail slides up from below', ja: 'メールが下から上がる' },
  'mail.filter.btn': { ko: '필터', en: 'Filter', ja: 'フィルタ' },
  'mail.filter.heading': { ko: '거르기 — 여럿이면 모두 만족', en: 'Filter — all selected must match', ja: '絞り込み — 複数はAND' },
  'mail.filter.reset': { ko: '모두 해제', en: 'Clear all', ja: 'すべて解除' },
  // 빠른 조회를 켠 동안 목록 제목 옆에 무엇을 보고 있는지 적는다.
  'mail.stat.viewing': { ko: '{k}만 보는 중', en: 'Showing {k} only', ja: '{k} のみ表示' },
  'mail.stat.viewing_off': { ko: '전체 보기로', en: 'Show all', ja: 'すべて表示' },
  'mail.filter.only_loaded': {
    ko: '지금 불러온 목록에서 거릅니다',
    en: 'Filters the mail loaded so far',
    ja: '現在読み込んだ分から絞ります',
  },
  'mail.group.heading': { ko: '묶어 보기 — 하나만', en: 'Group by — pick one', ja: 'グループ化 — 一つだけ' },
  'mail.group.none': { ko: '묶지 않음', en: 'No grouping', ja: 'グループ化しない' },
  'mail.group.date': { ko: '시간순', en: 'By time', ja: '時間順' },
  'mail.group.sender': { ko: '보낸사람 묶기', en: 'By sender', ja: '差出人ごと' },
  'mail.group.scope_note': {
    ko: '지금 불러온 목록 안에서 묶습니다',
    en: 'Groups within the mail loaded so far',
    ja: '現在読み込んだ分の中でまとめます',
  },
  'mail.bucket.today': { ko: '오늘', en: 'Today', ja: '今日' },
  'mail.bucket.yesterday': { ko: '어제', en: 'Yesterday', ja: '昨日' },
  'mail.bucket.week': { ko: '이번 주', en: 'This week', ja: '今週' },
  'mail.bucket.older': { ko: '이전', en: 'Earlier', ja: 'それ以前' },
  'mail.bucket.unknown': { ko: '날짜 없음', en: 'No date', ja: '日付なし' },
  'mail.group.collapse': { ko: '접기', en: 'Collapse', ja: '折りたたむ' },
  'mail.group.expand': { ko: '펼치기', en: 'Expand', ja: '展開' },
  // 목록 줄의 별 — **버튼의 이름은 할 일**이다. 지금 상태는 aria-pressed 가 말한다.
  'mail.row.star': { ko: '중요 표시', en: 'Star', ja: '重要マーク' },
  'mail.row.unstar': { ko: '중요 표시 해제', en: 'Unstar', ja: '重要マーク解除' },
  'mail.row.reply': { ko: '답장', en: 'Reply', ja: '返信' },
  'mail.row.popout': { ko: '팝업보기', en: 'Open in window', ja: '別ウィンドウ' },
  'mail.row.trash': { ko: '휴지통', en: 'Trash', ja: 'ゴミ箱' },
  'mail.row.mark_read': { ko: '읽음으로 표시', en: 'Mark as read', ja: '既読にする' },
  'mail.row.mark_unread': { ko: '안읽음으로 표시', en: 'Mark as unread', ja: '未読にする' },
  'mail.row.reply_external_na': {
    ko: '밖에서 온 메일에는 아직 답장할 수 없습니다',
    en: 'Cannot reply to external mail yet',
    ja: '外部メールにはまだ返信できません',
  },
  'mail.popout.aria': { ko: '팝업으로 연 메일', en: 'Mail popup', ja: 'メールのポップアップ' },
  'mail.popout.close': { ko: '팝업 닫기', en: 'Close popup', ja: 'ポップアップを閉じる' },
  'mail.detail.btn_full': { ko: '전체보기', en: 'Full view', ja: '全画面' },
  'mail.detail.btn_exit_full': { ko: '목록 보기', en: 'Show list', ja: '一覧に戻す' },
  'mail.detail.btn_close': { ko: '메일 닫기', en: 'Close mail', ja: 'メールを閉じる' },
  'mail.folder.all': { ko: '전체메일', en: 'All mail', ja: 'すべてのメール' },
  'mail.folder.self': { ko: '내게쓴메일함', en: 'To myself', ja: '自分宛て' },
  'mail.receipt.read_of': { ko: '읽음 {a}/{b}', en: 'Read {a}/{b}', ja: '既読 {a}/{b}' },
  'mail.receipt.heading': { ko: '수신확인', en: 'Read receipts', ja: '開封確認' },
  'mail.receipt.unread': { ko: '안 읽음', en: 'Unread', ja: '未読' },
  'mail.receipt.external_na': { ko: '확인 불가 · 외부', en: 'Unknown · external', ja: '確認不可・外部' },
  // 내가 만드는 메일함 (migration 114, 2026-08-21)
  /* ── 화면 머리 (정본 A3 · 2026-09-08) ─────────────────────────────── */
  'mail.head.crumb': { ko: '메일', en: 'Mail', ja: 'メール' },
  'mail.head.desc_default': { ko: '이 메일함의 메일입니다.', en: 'Mail in this folder.', ja: 'このフォルダーのメールです。' },
  'mail.head.desc_inbox': { ko: '받은 메일입니다.', en: 'Mail you received.', ja: '受信したメールです。' },
  'mail.head.desc_sent': { ko: '보낸 메일입니다.', en: 'Mail you sent.', ja: '送信したメールです。' },
  'mail.head.desc_draft': { ko: '쓰다 만 메일입니다 — 이어 쓰려면 누르세요.', en: 'Drafts — open one to keep writing.', ja: '下書きです。開くと続きを書けます。' },
  'mail.head.desc_star': { ko: '표시해 둔 메일입니다.', en: 'Mail you starred.', ja: 'スターを付けたメールです。' },
  'mail.head.desc_self': { ko: '나에게 보낸 메일입니다.', en: 'Mail you sent to yourself.', ja: '自分宛てに送ったメールです。' },
  'mail.head.desc_spam': { ko: '스팸으로 분류된 메일입니다.', en: 'Mail marked as spam.', ja: '迷惑メールに分類されたメールです。' },
  'mail.head.desc_trash': { ko: '치운 메일입니다 — 되살리거나 완전히 지울 수 있습니다.', en: 'Deleted mail — restore or remove for good.', ja: '削除したメールです。復元も完全削除もできます。' },
  'mail.head.desc_all': { ko: '메일함을 가리지 않고 모두 봅니다.', en: 'All mail across folders.', ja: 'すべてのフォルダーのメールです。' },
  'mail.head.help_title': { ko: '이 화면의 규칙', en: 'How this screen works', ja: 'この画面のルール' },
  'mail.head.help_stat': {
    ko: '왼쪽 요약 넷(안 읽음 · 표시 · 첨부 · 나에게)을 누르면 그것만 봅니다. 켜져 있으면 머리에 「…만 보는 중」이 적힙니다.',
    en: 'The four summary tiles on the left filter the list. While one is on, the header says what you are viewing.',
    ja: '左の要約4つを押すとその条件だけを表示します。オンの間はヘッダーに表示中の条件が出ます。',
  },
  'mail.head.help_group': {
    ko: '묶어 보기를 켜면 같은 제목의 오간 메일이 한 줄로 접힙니다 — 줄 수가 줄 뿐 메일이 사라지는 것은 아닙니다.',
    en: 'Grouping folds a back-and-forth thread into one row — nothing is removed, only collapsed.',
    ja: 'スレッド表示は同じ件名のやり取りを1行にまとめます。メールが消えるわけではありません。',
  },
  'mail.head.help_trash': {
    ko: '치운 메일은 휴지통에 남습니다. 보관기간은 「환경설정 > 메일함」에서 정합니다.',
    en: 'Deleted mail stays in Trash. Set how long in Settings › Folders.',
    ja: '削除したメールはゴミ箱に残ります。保存期間は「環境設定 > フォルダー」で設定します。',
  },
  'mail.folder.my': { ko: '내 메일함', en: 'My folders', ja: 'マイフォルダ' },
  'mail.folder.add': { ko: '메일함 만들기', en: 'New folder', ja: 'フォルダ作成' },
  'mail.folder.add_sub': { ko: '하위 메일함 추가', en: 'Add subfolder', ja: 'サブフォルダ追加' },
  'mail.folder.rename': { ko: '이름 바꾸기', en: 'Rename', ja: '名前を変更' },
  'mail.folder.delete': { ko: '메일함 삭제', en: 'Delete folder', ja: 'フォルダ削除' },
  'mail.folder.empty': { ko: '비우기', en: 'Empty', ja: '空にする' },
  'mail.folder.read_all': { ko: '모두 읽음', en: 'Mark all read', ja: 'すべて既読' },
  'mail.folder.manage': { ko: '관리', en: 'Manage', ja: '管理' },
  'mail.folder.name_placeholder': { ko: '메일함 이름', en: 'Folder name', ja: 'フォルダ名' },
  'mail.folder.too_deep': { ko: '메일함은 3단까지입니다', en: 'Up to 3 levels', ja: '3階層までです' },
  'mail.folder.move_here': { ko: '이 메일함으로 옮기기', en: 'Move here', ja: 'ここへ移動' },
  'mail.folder.move_by_sender': {
    ko: '{who} 이(가) 보낸 메일 모두 옮기기',
    en: 'Move all mail from {who}',
    ja: '{who} からのメールをすべて移動',
  },
  'mail.folder.move_by_sender_hint': {
    ko: '이 메일함에 있는 같은 주소의 메일을 다음 쪽에 있는 것까지 함께 옮깁니다.',
    en: 'Moves every mail from this address in this folder, including other pages.',
    ja: 'このフォルダにある同じアドレスのメールを、他のページの分もまとめて移動します。',
  },
  'mail.folder.move_failed': { ko: '옮기지 못했습니다', en: 'Could not move', ja: '移動できませんでした' },
  'mail.folder.move_inbox': { ko: '받은편지함으로 되돌리기', en: 'Back to inbox', ja: '受信箱に戻す' },
  'mail.folder.move_aria': { ko: '메일함으로 이동', en: 'Move to folder', ja: 'フォルダへ移動' },
  'mail.folder.no_folders': { ko: '만든 메일함이 없습니다', en: 'No folders yet', ja: 'フォルダがありません' },
  // 실패를 조용히 삼키면 "눌러도 아무 일이 안 일어난다" 가 된다 (2026-08-21).
  'mail.folder.err_load': {
    ko: '내 메일함을 불러오지 못했습니다.',
    en: 'Could not load your folders.',
    ja: 'マイフォルダを読み込めませんでした。',
  },
  'mail.folder.err_save': {
    ko: '메일함을 저장하지 못했습니다.',
    en: 'Could not save the folder.',
    ja: 'フォルダを保存できませんでした。',
  },
  'mail.folder.err_name_taken': {
    ko: '같은 자리에 같은 이름이 있습니다.',
    en: 'A folder with that name already exists here.',
    ja: '同じ場所に同じ名前があります。',
  },
  'mail.row.move': { ko: '이동', en: 'Move', ja: '移動' },
  // 비우기·삭제 확인
  'mail.confirm.empty_trash_title': { ko: '영원히 지웁니다', en: 'Delete permanently', ja: '完全に削除します' },
  'mail.confirm.empty_trash_desc': {
    ko: '이 메일함의 메일을 되돌릴 수 없게 지웁니다. 첨부도 함께 사라집니다.',
    en: 'Deletes the mail in this folder for good, attachments included.',
    ja: 'このフォルダのメールを完全に削除します（添付も）。',
  },
  'mail.confirm.empty_title': { ko: '이 메일함을 비웁니다', en: 'Empty this folder', ja: 'このフォルダを空にします' },
  'mail.confirm.empty_desc': {
    ko: '안의 메일을 휴지통으로 옮깁니다. 휴지통에서 되돌릴 수 있습니다.',
    en: 'Moves the mail to Trash. You can restore it from there.',
    ja: 'メールをゴミ箱へ移動します。あとで戻せます。',
  },
  'mail.confirm.delete_title': { ko: '메일함을 삭제합니다', en: 'Delete this folder', ja: 'フォルダを削除します' },
  'mail.confirm.delete_desc': {
    ko: '메일함과 하위 메일함이 사라집니다. 안에 있던 메일은 지워지지 않고 받은편지함으로 돌아갑니다.',
    en: 'The folder and its subfolders go away. The mail inside returns to the inbox.',
    ja: 'フォルダとサブフォルダが消えます。中のメールは受信箱に戻ります。',
  },
  'mail.confirm.empty_ok': { ko: '비우기', en: 'Empty', ja: '空にする' },
  'mail.confirm.delete_ok': { ko: '삭제', en: 'Delete', ja: '削除' },
  'mail.toast.read_all': { ko: '{n}통을 읽음으로 표시했습니다', en: 'Marked {n} as read', ja: '{n}件を既読にしました' },
  'mail.toast.moved': { ko: '{n}통을 옮겼습니다', en: 'Moved {n}', ja: '{n}件を移動しました' },
  'mail.colhead.select_all': { ko: '이 쪽 전체 고르기', en: 'Select this page', ja: 'このページを全選択' },
  'mail.colhead.sender': { ko: '보낸사람', en: 'From', ja: '差出人' },
  'mail.colhead.subject': { ko: '제목', en: 'Subject', ja: '件名' },
  'mail.colhead.date': { ko: '날짜', en: 'Date', ja: '日付' },
  'mail.pager.prev': { ko: '이전 쪽', en: 'Previous page', ja: '前のページ' },
  'mail.pager.next': { ko: '다음 쪽', en: 'Next page', ja: '次のページ' },
  'mail.pager.size_aria': { ko: '한 쪽에 보일 통수', en: 'Mail per page', ja: '1ページの件数' },
  'mail.pager.size_prefix': { ko: '{n}통씩', en: '{n} / page', ja: '{n}件ずつ' },
  'mail.quota.label': { ko: '내 저장용량', en: 'My storage', ja: 'マイストレージ' },
  'mail.split.aria': { ko: '목록과 메일의 경계', en: 'List and mail divider', ja: '一覧とメールの境界' },
  // 상세 머리의 답장 세 칸 (2026-08-21) — 여태 화면에 한글이 박혀 있었다.
  'mail.detail.btn_reply': { ko: '답장', en: 'Reply', ja: '返信' },
  'mail.detail.btn_reply_all': { ko: '전체답장', en: 'Reply all', ja: '全員に返信' },
  'mail.detail.btn_forward': { ko: '전달', en: 'Forward', ja: '転送' },
  'mail.detail.btn_reply_all_title': {
    ko: '보낸 사람과 받는 사람 모두에게 답장',
    en: 'Reply to the sender and all recipients',
    ja: '送信者と全受信者に返信',
  },
  // ----- 첨부 (2026-08-22) -----
  'mail.attach.count': { ko: '첨부 {n}개', en: '{n} attachments', ja: '添付 {n}件' },
  'mail.attach.images': { ko: '사진 {n}장', en: '{n} images', ja: '写真 {n}枚' },
  'mail.attach.view': { ko: '보기', en: 'View', ja: '表示' },
  'mail.attach.view_aria': { ko: '{name} 열어 보기', en: 'Open {name}', ja: '{name} を開く' },
  'mail.attach.download_aria': { ko: '{name} 받기', en: 'Download {name}', ja: '{name} をダウンロード' },
  'mail.attach.saving_n': { ko: '{i}/{n} 받는 중', en: 'Saving {i}/{n}', ja: '{i}/{n} 保存中' },
  'mail.viewer.loading': { ko: '여는 중…', en: 'Opening…', ja: '読み込み中…' },
  'mail.attach.save_all': { ko: '모두 받기', en: 'Save all', ja: 'すべて保存' },
  'mail.attach.saving': { ko: '받는 중…', en: 'Saving…', ja: '保存中...' },
  'mail.attach.some_failed': {
    ko: '{n}개는 받지 못했습니다 — 각 파일에서 다시 받아 주세요.',
    en: "{n} couldn't be saved — download them individually.",
    ja: '{n}件は保存できませんでした — 個別に保存してください。',
  },
  // ----- 작성 첨부 자리 (2026-08-23) — 어제 만들며 한글을 코드에 박아 둔 것 봉합 -----
  'mail.attach.title': { ko: '파일 첨부', en: 'Attach files', ja: 'ファイル添付' },
  'mail.attach.drop_hint': { ko: '끌어다 놓아도 됩니다', en: 'or drop files anywhere', ja: 'ドロップでも添付できます' },
  'mail.attach.drop_now': { ko: '여기에 놓으면 첨부됩니다', en: 'Drop to attach', ja: 'ここに置くと添付されます' },
  'mail.attach.retry': { ko: '다시 시도', en: 'Retry', ja: '再試行' },
  'mail.attach.per_file_max': { ko: '한 파일 최대 {max}', en: 'Up to {max} per file', ja: '1ファイル最大 {max}' },
  'mail.attach.drop_here': { ko: '여기로 파일을 끌어다 놓거나', en: 'Drop files here, or', ja: 'ここにファイルをドロップ、または' },
  'mail.attach.from_device': { ko: '내 컴퓨터에서', en: 'From my computer', ja: 'このPCから' },
  'mail.attach.from_workspace': { ko: '워크스페이스에서', en: 'From Workspace', ja: 'ワークスペースから' },
  'mail.attach.count_of': { ko: '{n} / {max}개', en: '{n} / {max}', ja: '{n} / {max}件' },
  'mail.attach.hint_multi': {
    ko: '한 번에 여러 개를 고를 수 있습니다. 워크스페이스에 이미 있는 파일은 다시 올리지 않습니다.',
    en: 'You can pick several at once. Files already in Workspace are not re-uploaded.',
    ja: '複数まとめて選べます。ワークスペースにあるファイルは再アップロードしません。',
  },
  'mail.attach.hint_full': {
    ko: '첨부는 최대 {max}개까지 넣을 수 있습니다.',
    en: 'You can attach up to {max} files.',
    ja: '添付は最大{max}件までです。',
  },
  'mail.attach.total': { ko: '합계 {size}', en: 'Total {size}', ja: '合計 {size}' },
  'mail.attach.uploading_pct': { ko: '{p}% 올리는 중', en: 'Uploading {p}%', ja: '{p}% アップロード中' },
  'mail.attach.remove_aria': { ko: '{name} 첨부 빼기', en: 'Remove attachment {name}', ja: '{name} を添付から外す' },
  // ----- 작성 화면 (2026-08-22) -----
  'mail.compose.close_aria': { ko: '작성 닫기', en: 'Close compose', ja: '作成を閉じる' },
  'mail.compose.back_to_list': { ko: '목록', en: 'Back', ja: '一覧' },
  'mail.compose.badge_self': { ko: '내게 쓰기', en: 'To myself', ja: '自分宛て' },
  'mail.compose.badge_reply': { ko: '답장', en: 'Reply', ja: '返信' },
  'mail.compose.badge_reply_all': { ko: '전체답장', en: 'Reply all', ja: '全員に返信' },
  'mail.compose.badge_forward': { ko: '전달', en: 'Forward', ja: '転送' },
  // ----- 상세: 치우기 전에 묻기 (2026-08-22) -----
  // 자동 삭제는 **스팸에만** 있다(MAIL_SPAM_RETENTION_DAYS=30). 휴지통에는 없으므로
  // "30일" 이라고 적으면 거짓말이 된다 — 문구를 둘로 나눠 쓰는 이유다.
  'mail.detail.confirm_trash_title': { ko: '휴지통으로 옮길까요?', en: 'Move to trash?', ja: 'ゴミ箱に移動しますか?' },
  'mail.detail.confirm_trash_desc': {
    ko: '「{subject}」\n휴지통에서 언제든 되돌릴 수 있습니다.',
    en: '"{subject}"\nYou can restore it from Trash at any time.',
    ja: '「{subject}」\nゴミ箱からいつでも戻せます。',
  },
  'mail.detail.confirm_trash_ok': { ko: '휴지통으로', en: 'Move to trash', ja: 'ゴミ箱へ' },
  'mail.bulk.spam_confirm_desc': {
    ko: '고른 {n}통을 스팸함으로 옮깁니다. 스팸함에서 30일이 지나면 자동으로 지워집니다.',
    en: 'Moves {n} selected mails to Spam. Items in Spam are deleted after 30 days.',
    ja: '選んだ{n}件を迷惑メールに移動します。30日後に自動削除されます。',
  },
  'mail.bulk.trash_confirm_title': { ko: '휴지통으로 옮길까요?', en: 'Move to trash?', ja: 'ゴミ箱に移動しますか?' },
  'mail.bulk.trash_confirm_desc': {
    ko: '고른 {n}통을 휴지통으로 옮깁니다. 휴지통에서 언제든 되돌릴 수 있습니다.',
    en: 'Moves {n} selected mails to Trash. You can restore them at any time.',
    ja: '選んだ{n}件をゴミ箱に移動します。いつでも戻せます。',
  },
  'mail.recipient.uses': { ko: '{n}번 주고받음', en: '{n} exchanges', ja: '{n}回やり取り' },
  'mail.sent.show_trashed': { ko: '치운 메일 보기', en: 'Show removed', ja: '片づけたメール' },
  'mail.sent.back_to_live': { ko: '보낸편지함으로 돌아가기', en: 'Back to Sent', ja: '送信済みに戻る' },
  // 보낸 메일·초안 (migration 115, 2026-08-23) — 하는 일이 달라 문구도 갈린다.
  'mail.detail.btn_trash_sent_aria': { ko: '내 보낸편지함에서 치우기', en: 'Remove from my Sent', ja: '送信済みから片づける' },
  'mail.detail.btn_restore_sent_aria': { ko: '보낸편지함으로 되돌리기', en: 'Restore to Sent', ja: '送信済みに戻す' },
  'mail.detail.btn_delete_draft_aria': { ko: '이 초안 지우기', en: 'Delete this draft', ja: 'この下書きを削除' },
  'mail.detail.confirm_sent_title': { ko: '내 보낸편지함에서 치울까요?', en: 'Remove from your Sent?', ja: '送信済みから片づけますか?' },
  'mail.detail.confirm_sent_desc': {
    ko: '「{subject}」\n받는 사람의 편지함에는 그대로 남습니다.\n보낸편지함의 \u2018치운 메일 보기\u2019 에서 되돌릴 수 있습니다.',
    en: '"{subject}"\nIt stays in the recipients\u2019 mailboxes.\nYou can restore it from “Show removed” in Sent.',
    ja: '「{subject}」\n受信者のメールボックスには残ります。\n送信済みの「片づけたメール」から戻せます。',
  },
  'mail.detail.confirm_sent_ok': { ko: '치우기', en: 'Remove', ja: '片づける' },
  'mail.detail.confirm_draft_title': { ko: '이 초안을 지울까요?', en: 'Delete this draft?', ja: 'この下書きを削除しますか?' },
  'mail.detail.confirm_draft_desc': {
    ko: '「{subject}」\n되돌릴 수 없습니다.',
    en: '"{subject}"\nThis cannot be undone.',
    ja: '「{subject}」\n元に戻せません。',
  },
  'mail.detail.confirm_draft_ok': { ko: '지우기', en: 'Delete', ja: '削除' },
  'mail.detail.confirm_spam_title': { ko: '스팸으로 옮길까요?', en: 'Move to spam?', ja: '迷惑メールに移動しますか?' },
  'mail.detail.confirm_spam_desc': {
    ko: '「{subject}」\n스팸함에서 30일이 지나면 자동으로 지워집니다.',
    en: '"{subject}"\nItems in Spam are deleted automatically after 30 days.',
    ja: '「{subject}」\n迷惑メールは30日後に自動削除されます。',
  },
  'mail.detail.confirm_spam_ok': { ko: '스팸으로', en: 'Move to spam', ja: '迷惑メールへ' },
  // ----- 목록: 본문 미리보기 · 줄 표식 (2026-08-22) -----
  'mail.preview.heading': { ko: '본문 미리보기', en: 'Body preview', ja: '本文プレビュー' },
  'mail.preview.label': { ko: '제목 뒤에 본문 보이기', en: 'Show body after subject', ja: '件名の後に本文を表示' },
  'mail.item.flag_attach_title': { ko: '첨부 있음', en: 'Has attachments', ja: '添付あり' },
  'mail.item.flag_cc_title': { ko: '참조로 받은 메일', en: 'Received as Cc', ja: 'CC で受信' },
  // ----- 상세: 받는사람 · 참조 (2026-08-22) -----
  // 여태 세 종류를 한 줄에 이어 붙여 "받는 사람:" 이라고 적었다. 종류는 서버가 이미 준다.
  // ★ 2026-08-23 — 상세와 **작성이 같은 낱말**을 쓴다.
  //   작성 화면은 여태 'To' · 'CC' · 'BCC' 를 코드에 박아 둬서 어느 언어로 봐도 영어였다
  //   (사용자 지적). 두 벌로 두면 한쪽만 고쳐지므로 이름을 화면 중립으로 바꿔 함께 쓴다.
  'mail.field.to': { ko: '받는사람', en: 'To', ja: '宛先' },
  'mail.field.cc': { ko: '참조', en: 'Cc', ja: 'CC' },
  'mail.field.bcc': { ko: '숨은참조', en: 'Bcc', ja: 'BCC' },
  'mail.detail.addr_me': { ko: '(나)', en: '(me)', ja: '(自分)' },
  'mail.detail.addr_more': { ko: '+{n}명', en: '+{n} more', ja: '他{n}名' },
  'mail.detail.addr_less': { ko: '접기', en: 'Show less', ja: '折りたたむ' },
  // ══ 목록 밀도 (2026-09-05) — 「환경설정 > 기본환경」과 **같은 값**을 본다 ══
  'mail.density.heading': { ko: '목록 밀도', en: 'List density', ja: '一覧の密度' },
  'mail.density.narrow': { ko: '좁게', en: 'Compact', ja: '狭く' },
  'mail.density.wide': { ko: '넓게', en: 'Comfortable', ja: '広く' },
  'mail.density.narrow_desc': {
    ko: '압축형 목록 — 아바타·본문 미리보기를 보이지 않습니다',
    en: 'Compact list — no avatar or body preview',
    ja: '圧縮表示 — アバターと本文プレビューを表示しません',
  },
  'mail.density.wide_desc': {
    ko: '아바타·본문 미리보기를 함께 보입니다',
    en: 'Shows avatar and body preview',
    ja: 'アバターと本文プレビューを一緒に表示します',
  },

  // ══ 환경설정 (2026-09-05) — 정본 P2-1 설정 화면 ══
  'mail.nav.settings': { ko: '환경설정', en: 'Settings', ja: '環境設定' },
  'mail.settings.title': { ko: '환경설정', en: 'Mail settings', ja: '環境設定' },
  'mail.settings.desc': {
    ko: '메일을 읽고 쓰는 방식을 정합니다.',
    en: 'Choose how you read and write mail.',
    ja: 'メールの読み書きの方法を決めます。',
  },
  'mail.settings.help_tab': {
    ko: '탭마다 따로 저장됩니다 — 한 탭에서 고친 것은 그 탭의 「저장」을 눌러야 반영됩니다.',
    en: 'Each tab saves on its own — press Save on the tab you edited.',
    ja: 'タブごとに保存します。編集したタブの「保存」を押してください。',
  },
  'mail.settings.help_scope': {
    ko: '여기서 정한 것은 이 계정에만 적용됩니다. 회사 전체 설정은 관리자가 정합니다.',
    en: 'These settings apply to your account only. Company-wide settings are set by an admin.',
    ja: 'ここでの設定はこのアカウントのみに適用されます。全社設定は管理者が行います。',
  },
  'mail.settings.back': { ko: '메일함으로', en: 'Back to mail', ja: 'メールへ戻る' },
  'mail.settings.tabs_aria': { ko: '환경설정 항목', en: 'Settings sections', ja: '環境設定の項目' },
  'mail.settings.tab.basic': { ko: '기본환경', en: 'General', ja: '基本環境' },
  'mail.settings.tab.folder': { ko: '메일함', en: 'Folders', ja: 'メールボックス' },
  'mail.settings.tab.spam': { ko: '스팸', en: 'Spam', ja: '迷惑メール' },
  'mail.settings.tab.filter': { ko: '자동분류', en: 'Filters', ja: '自動振り分け' },
  'mail.settings.tab.forward': { ko: '자동전달', en: 'Forwarding', ja: '自動転送' },
  'mail.settings.tab.vacation': { ko: '부재중응답', en: 'Auto reply', ja: '不在応答' },
  'mail.settings.tab.external': { ko: '외부메일', en: 'External mail', ja: '外部メール' },
  'mail.settings.tab.recent': { ko: '최근보낸메일', en: 'Recently sent', ja: '最近送ったメール' },
  'mail.settings.soon': { ko: '아직 만들지 않았습니다', en: 'Not built yet', ja: 'まだ作っていません' },
  'mail.settings.soon_desc': {
    ko: '이 칸은 기본환경 다음 판에서 만듭니다. 지금은 빈 자리입니다.',
    en: 'This section comes after General in the next round. It is empty for now.',
    ja: 'この項目は基本環境の次の版で作ります。今は空です。',
  },
  'mail.settings.sec.read': { ko: '메일 읽기 설정', en: 'Reading', ja: 'メールを読む設定' },
  'mail.settings.sec.write': { ko: '메일 쓰기 설정', en: 'Writing', ja: 'メールを書く設定' },

  // ── 읽기 ──
  'mail.settings.addr_display': { ko: '메일주소 표시방식', en: 'Address display', ja: 'メールアドレスの表示' },
  'mail.settings.addr_name': { ko: '이름', en: 'Name only', ja: '名前' },
  'mail.settings.addr_name_email': { ko: '이름 + 메일주소', en: 'Name + address', ja: '名前 + メールアドレス' },
  'mail.settings.images': { ko: '본문 이미지 처리', en: 'Body images', ja: '本文の画像' },
  'mail.settings.images_label': {
    ko: '본문 내 이미지를 보여주지 않음',
    en: 'Do not show images in the body',
    ja: '本文内の画像を表示しない',
  },
  'mail.settings.unsafe': { ko: '유해태그 차단', en: 'Unsafe tag blocking', ja: '有害タグの遮断' },
  'mail.settings.unsafe_label': {
    ko: '본문 내 보안상 위험이 있는 태그를 비활성화 함',
    en: 'Disable security-risk tags in the body',
    ja: '本文内のセキュリティ上危険なタグを無効にする',
  },
  'mail.settings.unsafe_hint': {
    ko: '차단 태그 — html · head · meta · body · script · iframe · embed. 끄면 본문에 심어 둔 코드가 그대로 도므로 잠가 둡니다.',
    en: 'Blocked tags — html · head · meta · body · script · iframe · embed. Locked on: turning it off would run code embedded in the body.',
    ja: '遮断タグ — html · head · meta · body · script · iframe · embed。切ると本文に仕込まれたコードが動くため固定です。',
  },
  'mail.settings.origin': { ko: '국가표시', en: 'Origin country', ja: '国表示' },
  'mail.settings.origin_label': {
    ko: '메일이 전송된 경로와 국가를 표시함',
    en: 'Show the route and country the mail came through',
    ja: 'メールが送られた経路と国を表示する',
  },
  'mail.settings.origin_hint': {
    ko: '받은 경로 정보가 있는 메일에만 보입니다 — 지금은 경로를 저장하지 않아 아무 메일에도 뜨지 않습니다.',
    en: 'Shown only on mail that carries route information — routes are not stored yet, so nothing appears today.',
    ja: '経路情報のあるメールにだけ表示されます — 今は経路を保存していないため、どのメールにも出ません。',
  },
  'mail.settings.search_scope': { ko: '검색 범위 설정', en: 'Search scope', ja: '検索範囲' },
  'mail.settings.search_scope_label': {
    ko: '스팸메일함과 휴지통 포함',
    en: 'Include spam and trash',
    ja: '迷惑メールとゴミ箱を含む',
  },
  'mail.settings.list_screen': { ko: '메일 리스트 화면', en: 'Mail list', ja: 'メール一覧の画面' },
  'mail.settings.list_cc_label': { ko: '참조 표시', en: 'Show Cc', ja: 'CC を表示' },
  'mail.settings.density': { ko: '메일 목록 밀도', en: 'List density', ja: 'メール一覧の密度' },
  'mail.settings.density_hint': {
    ko: '목록 위 «열림 설정» 단추의 밀도와 같은 값입니다 — 어느 쪽에서 바꿔도 함께 바뀝니다.',
    en: 'Same value as the density in the View button above the list — changing either updates both.',
    ja: '一覧上部の View ボタンの密度と同じ値です — どちらで変えても一緒に変わります。',
  },

  // ── 쓰기 ──
  'mail.settings.recipient_input': {
    ko: '받는사람(참조·숨은참조) 추가방식',
    en: 'Adding recipients (Cc · Bcc)',
    ja: '宛先(CC・BCC)の追加方法',
  },
  'mail.settings.recipient_auto': { ko: '받는사람 자동완성', en: 'Autocomplete', ja: '宛先の自動補完' },
  'mail.settings.recipient_name': { ko: '받는사람 이름만 입력', en: 'Type the name only', ja: '宛先の名前のみ入力' },
  'mail.settings.recipient_search': { ko: '받는사람을 검색하여 입력', en: 'Search and pick', ja: '宛先を検索して入力' },
  'mail.settings.send_confirm': { ko: '발송 전 확인 팝업', en: 'Confirm before sending', ja: '送信前の確認ポップアップ' },
  'mail.settings.send_confirm_on': { ko: '팝업 사용', en: 'Use the popup', ja: 'ポップアップを使う' },
  'mail.settings.send_confirm_off': { ko: '팝업 사용하지 않음', en: 'No popup', ja: 'ポップアップを使わない' },
  'mail.settings.send_confirm_hint': {
    ko: '보내기를 누른 뒤 팝업으로 발송 내용을 다시 확인하거나, 확인 없이 바로 보냅니다.',
    en: 'After pressing Send, review the mail in a popup — or send it right away.',
    ja: '送信を押した後、ポップアップで内容を確認するか、確認せずにすぐ送ります。',
  },
  'mail.settings.save_sent': { ko: '보낸메일함 저장', en: 'Save to Sent', ja: '送信済みに保存' },
  'mail.settings.save_sent_on': { ko: '저장함', en: 'Save', ja: '保存する' },
  'mail.settings.save_sent_off': { ko: '저장하지 않음', en: 'Do not save', ja: '保存しない' },
  'mail.settings.receipt': { ko: '수신확인 기능', en: 'Read receipts', ja: '開封確認' },
  'mail.settings.use_on': { ko: '사용함', en: 'On', ja: '使う' },
  'mail.settings.use_off': { ko: '사용하지 않음', en: 'Off', ja: '使わない' },
  'mail.settings.editor': { ko: '편집기 선택', en: 'Editor', ja: 'エディタの選択' },
  'mail.settings.editor_html': { ko: 'HTML 편집기', en: 'html', ja: 'html' },
  'mail.settings.editor_text': { ko: '텍스트 편집기', en: 'text', ja: 'text' },
  'mail.settings.editor_md': { ko: 'markdown', en: 'markdown', ja: 'markdown' },
  'mail.settings.compose_window': { ko: '메일쓰기 방식', en: 'Compose window', ja: 'メール作成の方式' },
  'mail.settings.compose_page': { ko: '일반쓰기', en: 'Full page', ja: '通常作成' },
  'mail.settings.compose_popup': { ko: '팝업쓰기', en: 'Popup', ja: 'ポップアップ作成' },
  'mail.settings.encoding': { ko: '메일 인코딩', en: 'Encoding', ja: 'メールのエンコード' },
  'mail.settings.draft_alert': { ko: '임시보관함 알림', en: 'Draft reminder', ja: '下書きの通知' },
  'mail.settings.sender_name': { ko: '보내는사람 이름', en: 'Sender name', ja: '差出人の名前' },
  'mail.settings.sender_name_ph': { ko: '비우면 계정 이름을 씁니다', en: 'Leave empty to use your account name', ja: '空ならアカウント名を使います' },
  'mail.settings.reply_to': { ko: '답장받을 메일주소', en: 'Reply-to address', ja: '返信を受け取るアドレス' },
  'mail.settings.reply_to_ph': { ko: '비우면 보낸 주소로 받습니다', en: 'Leave empty to use your own address', ja: '空なら送信アドレスで受け取ります' },
  'mail.settings.reply_to_err': { ko: '메일주소 모양이 아닙니다 — 이름@도메인 으로 적습니다', en: 'Not an email address — use name@domain', ja: 'メールアドレスの形ではありません — 名前@ドメイン' },
  'mail.settings.vcard': { ko: '명함 첨부', en: 'Attach business card', ja: '名刺の添付' },
  'mail.settings.vcard_label': {
    ko: 'MS 제품군 공통 규격(v-card) 사용',
    en: 'Use the v-card format shared with MS products',
    ja: 'MS 製品共通規格(v-card)を使う',
  },
  'mail.settings.vcard_manage': { ko: '명함 관리', en: 'Manage card', ja: '名刺の管理' },
  'mail.settings.pending': {
    ko: '아래 값은 저장되지만 발송에는 아직 반영되지 않습니다 — 보낸메일함 저장 · 수신확인 · 인코딩 · 임시보관함 알림 · 보내는사람 이름 · 답장받을 메일주소 · 명함 첨부. 발송 경로를 고치는 다음 판에서 함께 붙습니다.',
    en: 'The values below are saved but do not affect sending yet — Save to Sent · Read receipts · Encoding · Draft reminder · Sender name · Reply-to · Attach card. They land with the next round that changes the send path.',
    ja: '以下の値は保存されますが、まだ送信には反映されません — 送信済み保存・開封確認・エンコード・下書き通知・差出人名・返信先・名刺添付。送信経路を直す次の版で一緒に付きます。',
  },
  'mail.settings.pending_read': {
    ko: '아래 둘은 저장되지만 아직 화면에 반영되지 않습니다 — 검색 범위 · 참조 표시. 서버가 그 값을 줄 수 있게 되는 다음 판에서 붙습니다.',
    en: 'Two below are saved but not applied yet — Search scope · Show Cc. They land once the server can provide those.',
    ja: '以下の二つは保存されますが、まだ画面に反映されません — 検索範囲・CC 表示。サーバーがその値を返せる次の版で付きます。',
  },
  'mail.settings.save': { ko: '저장', en: 'Save', ja: '保存' },
  'mail.settings.revert': { ko: '되돌리기', en: 'Revert', ja: '元に戻す' },
  'mail.settings.saved': { ko: '저장됨 · 방금', en: 'Saved · just now', ja: '保存しました・たった今' },
  'mail.settings.saving': { ko: '저장하는 중…', en: 'Saving…', ja: '保存中…' },
  'mail.settings.save_failed': {
    ko: '저장하지 못했습니다 — 잠시 뒤 다시 눌러 주세요.',
    en: 'Could not save — try again in a moment.',
    ja: '保存できませんでした — 少し後にもう一度押してください。',
  },
  'mail.settings.legacy': {
    ko: '서버가 아직 이 설정을 모릅니다 — 서버를 올린 뒤에 저장됩니다.',
    en: 'The server does not know these settings yet — they save after the server is updated.',
    ja: 'サーバーがまだこの設定を知りません — サーバー更新後に保存されます。',
  },
  'mail.settings.load_failed': {
    ko: '설정을 불러오지 못했습니다 — 기본값을 보여 줍니다.',
    en: 'Could not load settings — showing the defaults.',
    ja: '設定を読み込めませんでした — 既定値を表示します。',
  },

  // ── 명함 관리 팝업 ──
  'mail.vcard.title': { ko: '명함 관리', en: 'Business card', ja: '名刺の管理' },
  'mail.vcard.photo': { ko: '사진', en: 'Photo', ja: '写真' },
  'mail.vcard.photo_hint': {
    ko: '프로필 사진을 씁니다 — 바꾸려면 내 프로필에서 바꿉니다.',
    en: 'Uses your profile photo — change it in your profile.',
    ja: 'プロフィール写真を使います — 変更はプロフィールで行います。',
  },
  'mail.vcard.f.name': { ko: '이름표시', en: 'Display name', ja: '表示名' },
  'mail.vcard.f.org': { ko: '소속', en: 'Organization', ja: '所属' },
  'mail.vcard.f.email': { ko: '이메일', en: 'Email', ja: 'メール' },
  'mail.vcard.f.mobile': { ko: '휴대폰', en: 'Mobile', ja: '携帯' },
  'mail.vcard.f.officeTel': { ko: '회사전화', en: 'Office phone', ja: '会社電話' },
  'mail.vcard.f.officeAddr': { ko: '회사주소', en: 'Office address', ja: '会社住所' },
  'mail.vcard.f.memo': { ko: '메모', en: 'Memo', ja: 'メモ' },
  'mail.vcard.f.homeTel': { ko: '집전화', en: 'Home phone', ja: '自宅電話' },
  'mail.vcard.f.homeAddr': { ko: '집주소', en: 'Home address', ja: '自宅住所' },
  'mail.vcard.f.messenger': { ko: '메신저', en: 'Messenger', ja: 'メッセンジャー' },
  'mail.vcard.f.homepage': { ko: '회사홈페이지', en: 'Company site', ja: '会社ホームページ' },
  'mail.vcard.add': { ko: '항목 추가', en: 'Add field', ja: '項目を追加' },
  'mail.vcard.add_aria': { ko: '명함에 더할 항목 고르기', en: 'Pick a field to add', ja: '名刺に追加する項目を選ぶ' },
  'mail.vcard.remove': { ko: '빼기', en: 'Remove', ja: '外す' },
  'mail.vcard.all_added': { ko: '더할 항목이 없습니다', en: 'No fields left to add', ja: '追加できる項目がありません' },
  'mail.vcard.done': { ko: '확인', en: 'Done', ja: 'OK' },
  'mail.vcard.cancel': { ko: '취소', en: 'Cancel', ja: 'キャンセル' },

  // ── 발송 전 확인 팝업 ──
  'mail.send.confirm_title': { ko: '이대로 보낼까요?', en: 'Send this mail?', ja: 'このまま送りますか？' },
  'mail.send.confirm_ok': { ko: '보내기', en: 'Send', ja: '送信' },
  'mail.send.confirm_desc': {
    ko: '제목 「{s}」 · 받는사람 {n}명에게 보냅니다. 보낸 메일은 되돌릴 수 없습니다.',
    en: 'Subject “{s}” · going to {n} recipient(s). Sent mail cannot be recalled.',
    ja: '件名「{s}」・宛先 {n} 名に送ります。送ったメールは取り消せません。',
  },

  // ══ 환경설정 > 메일함 (migration 159, 2026-09-05) ══
  'mail.mbox.heading': { ko: '메일함', en: 'Mailboxes', ja: 'メールボックス' },
  'mail.mbox.add': { ko: '메일함 추가', en: 'Add mailbox', ja: 'メールボックスを追加' },
  'mail.mbox.col.name': { ko: '메일함 목록', en: 'Mailbox', ja: 'メールボックス' },
  'mail.mbox.col.retention': { ko: '보관기간', en: 'Keep for', ja: '保管期間' },
  'mail.mbox.col.count': { ko: '안읽은/전체', en: 'Unread/Total', ja: '未読/全体' },
  'mail.mbox.col.size': { ko: '사용량', en: 'Size', ja: '使用量' },
  'mail.mbox.col.manage': { ko: '관리', en: 'Manage', ja: '管理' },
  'mail.mbox.unlimited': { ko: '무제한', en: 'Forever', ja: '無期限' },
  'mail.mbox.r.1m': { ko: '1개월', en: '1 month', ja: '1か月' },
  'mail.mbox.r.3m': { ko: '3개월', en: '3 months', ja: '3か月' },
  'mail.mbox.r.6m': { ko: '6개월', en: '6 months', ja: '6か月' },
  'mail.mbox.r.1y': { ko: '1년', en: '1 year', ja: '1年' },
  'mail.mbox.r.3y': { ko: '3년', en: '3 years', ja: '3年' },
  'mail.mbox.r.5y': { ko: '5년', en: '5 years', ja: '5年' },
  'mail.mbox.r.days': { ko: '{n}일', en: '{n} days', ja: '{n}日' },
  'mail.mbox.system_note': {
    ko: '시스템 메일함은 보관기간이 무제한이고 지울 수 없습니다. 스팸함만 30일 뒤 자동으로 비워집니다.',
    en: 'System mailboxes are kept forever and cannot be deleted. Only Spam is emptied automatically after 30 days.',
    ja: 'システムのメールボックスは無期限で、削除できません。迷惑メールだけ30日後に自動で空になります。',
  },
  'mail.mbox.size_note': {
    ko: '사용량은 첨부 파일의 합입니다 — 내 저장용량과 같은 잣대입니다.',
    en: 'Size counts attachments only — the same measure as My storage.',
    ja: '使用量は添付ファイルの合計です — マイストレージと同じ基準です。',
  },
  'mail.mbox.backup': { ko: '백업', en: 'Back up', ja: 'バックアップ' },
  'mail.mbox.empty': { ko: '비우기', en: 'Empty', ja: '空にする' },
  'mail.mbox.more': { ko: '더 보기', en: 'More', ja: 'その他' },
  'mail.mbox.move': { ko: '이동', en: 'Move', ja: '移動' },
  'mail.mbox.share': { ko: '공유', en: 'Share', ja: '共有' },
  'mail.mbox.upload': { ko: '업로드', en: 'Upload', ja: 'アップロード' },
  'mail.mbox.delete': { ko: '삭제', en: 'Delete', ja: '削除' },
  'mail.mbox.not_yet': {
    ko: '아직 만들지 않았습니다 — 다음 판에 붙습니다.',
    en: 'Not built yet — lands in the next round.',
    ja: 'まだ作っていません — 次の版で付きます。',
  },
  'mail.mbox.locked_note': {
    ko: '백업 · 공유 · 업로드는 아직 서버에 길이 없어 잠가 두었습니다 — 다음 판에 붙습니다.',
    en: 'Back up · Share · Upload are locked — the server has no path for them yet.',
    ja: 'バックアップ・共有・アップロードはサーバー側がまだないため固定しています。',
  },
  'mail.mbox.system_locked': {
    ko: '시스템 메일함이라 할 수 없습니다',
    en: 'Not available for system mailboxes',
    ja: 'システムのメールボックスではできません',
  },
  'mail.mbox.retention_aria': { ko: '{n} 보관기간', en: 'Keep {n} for', ja: '{n} の保管期間' },
  'mail.mbox.manage_aria': { ko: '{n} 관리', en: 'Manage {n}', ja: '{n} の管理' },
  'mail.mbox.none': {
    ko: '아직 만든 메일함이 없습니다 — 「메일함 추가」로 만듭니다.',
    en: 'No mailboxes yet — use “Add mailbox”.',
    ja: 'まだ作ったメールボックスがありません — 「メールボックスを追加」から。',
  },
  // ── 메일함 추가 팝업 ──
  'mail.mbox.add_title': { ko: '메일함 추가', en: 'Add mailbox', ja: 'メールボックスを追加' },
  'mail.mbox.name_label': { ko: '메일함 이름', en: 'Name', ja: 'メールボックス名' },
  'mail.mbox.name_ph': { ko: '예: 거래처', en: 'e.g. Clients', ja: '例: 取引先' },
  'mail.mbox.parent_label': { ko: '상위 메일함', en: 'Parent mailbox', ja: '上位メールボックス' },
  'mail.mbox.parent_ph': { ko: '메일함을 고르세요', en: 'Pick a mailbox', ja: 'メールボックスを選んでください' },
  /* 3단 제한은 서버가 정한다 — 4단째를 골라 놓고 「만들기」에서야 막히면 늦다. */
  'mail.mbox.parent_hint': {
    ko: '메일함은 3단까지 만들 수 있습니다.',
    en: 'Mailboxes go three levels deep.',
    ja: 'メールボックスは3段まで作れます。',
  },
  'mail.mbox.create': { ko: '만들기', en: 'Create', ja: '作成' },
  'mail.mbox.cancel': { ko: '취소', en: 'Cancel', ja: 'キャンセル' },
  'mail.mbox.err_name': { ko: '메일함 이름을 적어 주세요', en: 'Enter a name', ja: 'メールボックス名を入力してください' },
  'mail.mbox.err_parent': { ko: '상위 메일함을 고르세요', en: 'Pick a parent mailbox', ja: '上位メールボックスを選んでください' },
  'mail.mbox.err_taken': { ko: '같은 자리에 같은 이름이 있습니다', en: 'A mailbox with that name is already there', ja: '同じ場所に同じ名前があります' },
  'mail.mbox.err_deep': { ko: '메일함은 3단까지입니다', en: 'Mailboxes go three levels deep', ja: 'メールボックスは3段までです' },
  'mail.mbox.err_failed': { ko: '만들지 못했습니다 — 잠시 뒤 다시 해 주세요', en: 'Could not create — try again in a moment', ja: '作成できませんでした — 少し後にもう一度' },
  'mail.mbox.save_failed': { ko: '바꾸지 못했습니다 — 잠시 뒤 다시 해 주세요', en: 'Could not change — try again in a moment', ja: '変更できませんでした — 少し後にもう一度' },
  // ── 지우기 · 비우기 ──
  'mail.mbox.del_title': { ko: '「{n}」을 지울까요?', en: 'Delete “{n}”?', ja: '「{n}」を削除しますか？' },
  'mail.mbox.del_desc': {
    ko: '메일함만 지웁니다 — 담겨 있던 메일은 받은편지함으로 돌아갑니다.',
    en: 'Only the mailbox is removed — the mail inside goes back to the Inbox.',
    ja: 'メールボックスだけ削除します — 中のメールは受信箱に戻ります。',
  },
  'mail.mbox.del_desc_kids': {
    ko: '하위 메일함 {k}개도 함께 사라집니다. 담겨 있던 메일은 받은편지함으로 돌아갑니다.',
    en: '{k} mailbox(es) under it disappear too. The mail inside goes back to the Inbox.',
    ja: '下位のメールボックス {k} 件も一緒に消えます。中のメールは受信箱に戻ります。',
  },
  'mail.mbox.empty_title': { ko: '「{n}」을 비울까요?', en: 'Empty “{n}”?', ja: '「{n}」を空にしますか？' },
  'mail.mbox.empty_desc': {
    ko: '담긴 메일이 휴지통으로 갑니다. 휴지통에서 되돌릴 수 있습니다.',
    en: 'The mail inside moves to Trash. You can restore it from there.',
    ja: '中のメールはゴミ箱に移動します。ゴミ箱から戻せます。',
  },
  // ══ 태그 ══
  'mail.tag.heading': { ko: '태그', en: 'Tags', ja: 'タグ' },
  'mail.tag.add': { ko: '태그 추가', en: 'Add tag', ja: 'タグを追加' },
  'mail.tag.col.tag': { ko: '태그', en: 'Tag', ja: 'タグ' },
  'mail.tag.col.manage': { ko: '관리', en: 'Manage', ja: '管理' },
  'mail.tag.none': {
    ko: '아직 만든 태그가 없습니다 — 「태그 추가」로 만듭니다.',
    en: 'No tags yet — use “Add tag”.',
    ja: 'まだタグがありません — 「タグを追加」から。',
  },
  'mail.tag.not_attached': {
    ko: '태그를 메일에 붙이는 것은 다음 판입니다 — 지금은 만들어 두기만 합니다.',
    en: 'Putting tags on mail comes next — for now you only create them.',
    ja: 'メールにタグを付けるのは次の版です — 今は作るところまでです。',
  },
  'mail.tag.add_title': { ko: '태그 추가', en: 'Add tag', ja: 'タグを追加' },
  'mail.tag.edit_title': { ko: '태그 수정', en: 'Edit tag', ja: 'タグの修正' },
  'mail.tag.name_label': { ko: '태그 이름', en: 'Name', ja: 'タグ名' },
  'mail.tag.name_ph': { ko: '예: 급함', en: 'e.g. Urgent', ja: '例: 至急' },
  'mail.tag.color_label': { ko: '태그 색상', en: 'Color', ja: 'タグの色' },
  'mail.tag.preview': { ko: '미리보기', en: 'Preview', ja: 'プレビュー' },
  'mail.tag.edit': { ko: '수정', en: 'Edit', ja: '修正' },
  'mail.tag.delete': { ko: '삭제', en: 'Delete', ja: '削除' },
  'mail.tag.save': { ko: '저장', en: 'Save', ja: '保存' },
  'mail.tag.del_title': { ko: '태그 「{n}」을 지울까요?', en: 'Delete tag “{n}”?', ja: 'タグ「{n}」を削除しますか？' },
  'mail.tag.del_desc': {
    ko: '지운 태그는 되돌릴 수 없습니다.',
    en: 'A deleted tag cannot be restored.',
    ja: '削除したタグは元に戻せません。',
  },
  'mail.tag.err_name': { ko: '태그 이름을 적어 주세요', en: 'Enter a name', ja: 'タグ名を入力してください' },
  'mail.tag.err_taken': { ko: '같은 이름의 태그가 있습니다', en: 'A tag with that name already exists', ja: '同じ名前のタグがあります' },
  'mail.tag.err_failed': { ko: '저장하지 못했습니다 — 잠시 뒤 다시 해 주세요', en: 'Could not save — try again in a moment', ja: '保存できませんでした — 少し後にもう一度' },
  'mail.tag.c.blue': { ko: '파랑', en: 'Blue', ja: '青' },
  'mail.tag.c.orange': { ko: '주황', en: 'Orange', ja: 'オレンジ' },
  'mail.tag.c.purple': { ko: '보라', en: 'Purple', ja: '紫' },
  'mail.tag.c.green': { ko: '초록', en: 'Green', ja: '緑' },
  'mail.tag.c.pink': { ko: '분홍', en: 'Pink', ja: 'ピンク' },
  'mail.tag.c.red': { ko: '빨강', en: 'Red', ja: '赤' },
  'mail.tag.c.teal': { ko: '청록', en: 'Teal', ja: '青緑' },

  /* ── 환경설정 > 스팸 (migration 160, 2026-09-05) ──
     ★ 등급 도움말은 서버 `mail-spam-policy.ts` 가 실제로 거르는 것과 **같은 말**이어야 한다.
       화면이 지어내면 「높음으로 뒀는데 왜 안 걸리나」를 아무도 못 짚는다(정본 P8). */
  'mail.spamset.sec.basic': { ko: '스팸 기본설정', en: 'Spam basics', ja: '迷惑メール基本設定' },
  'mail.spamset.policy': { ko: '스팸차단 정책', en: 'Spam policy', ja: '迷惑メール遮断ポリシー' },
  'mail.spamset.p.auto': {
    ko: '자동스팸차단 설정 사용함',
    en: 'Use automatic spam blocking',
    ja: '自動迷惑メール遮断を使用する',
  },
  'mail.spamset.p.off': {
    ko: '자동스팸차단 설정 사용안함',
    en: 'Do not use automatic spam blocking',
    ja: '自動迷惑メール遮断を使用しない',
  },
  'mail.spamset.p.all_receive': { ko: '모든메일 수신', en: 'Receive all mail', ja: 'すべてのメールを受信' },
  'mail.spamset.p.all_block': { ko: '모든메일 차단', en: 'Block all mail', ja: 'すべてのメールを遮断' },
  'mail.spamset.p.allow_only': {
    ko: '수신허용된 메일만 수신',
    en: 'Receive only allowed senders',
    ja: '受信許可したメールのみ受信',
  },
  'mail.spamset.level': { ko: '차단 등급', en: 'Blocking level', ja: '遮断レベル' },
  'mail.spamset.l.high': {
    ko: '높음 등급 (대부분의 스팸성 메일을 처리함, 권장값)',
    en: 'High (handles most spam-like mail, recommended)',
    ja: '高レベル（ほとんどの迷惑メールを処理・推奨）',
  },
  'mail.spamset.l.medium': {
    ko: '중간 등급 (일반적인 광고메일을 처리함)',
    en: 'Medium (handles ordinary advertising mail)',
    ja: '中レベル（一般的な広告メールを処理）',
  },
  'mail.spamset.l.low': {
    ko: '낮은 등급 (일부 악성 스팸메일만 처리함)',
    en: 'Low (handles only clearly malicious spam)',
    ja: '低レベル（一部の悪質な迷惑メールのみ処理）',
  },
  'mail.spamset.lh.high': {
    ko: '중간 등급이 거르는 것에 더해, 스팸성 낱말·눈길 끌기 기호·대량 발송 신호가 두 가지 이상 겹치는 메일을 거릅니다.',
    en: 'Everything Medium blocks, plus mail where two or more spam signals overlap (spam words, attention-grabbing symbols, bulk-send traces).',
    ja: '中レベルが遮断するものに加え、迷惑メール的な語・記号・一斉送信の痕跡が2つ以上重なるメールを遮断します。',
  },
  'mail.spamset.lh.medium': {
    ko: '낮은 등급이 거르는 것에 더해, 제목에 「(광고)」 표기가 있는 메일을 거릅니다.',
    en: 'Everything Low blocks, plus mail whose subject carries the mandatory “(ad)” label.',
    ja: '低レベルが遮断するものに加え、件名に「（広告）」表記があるメールを遮断します。',
  },
  'mail.spamset.lh.low': {
    ko: '실행되는 첨부파일(exe · scr · js 등)이 붙은 메일만 거릅니다.',
    en: 'Blocks only mail carrying executable attachments (exe, scr, js, …).',
    ja: '実行される添付ファイル（exe・scr・js など）が付いたメールのみ遮断します。',
  },
  'mail.spamset.action': { ko: '스팸메일 처리방법', en: 'What to do with spam', ja: '迷惑メールの処理方法' },
  'mail.spamset.a.spam_box': { ko: '스팸메일함으로 보내기', en: 'Move to the spam folder', ja: '迷惑メールフォルダへ' },
  'mail.spamset.a.trash': { ko: '휴지통으로 보내기', en: 'Move to trash', ja: 'ゴミ箱へ' },
  'mail.spamset.a.delete': { ko: '완전삭제', en: 'Delete permanently', ja: '完全削除' },
  'mail.spamset.ah.spam_box': {
    ko: '스팸메일함의 메일은 30일 뒤 자동으로 지워집니다. 그전에는 복원할 수 있습니다.',
    en: 'Spam is deleted automatically after 30 days. Until then you can restore it.',
    ja: '迷惑メールフォルダのメールは30日後に自動削除されます。それまでは元に戻せます。',
  },
  'mail.spamset.ah.delete': {
    ko: '완전삭제한 메일은 어디에도 남지 않습니다 — 잘못 걸러져도 되돌릴 수 없습니다.',
    en: 'Permanently deleted mail is kept nowhere — a mistake cannot be undone.',
    ja: '完全削除したメールはどこにも残りません — 誤判定でも元に戻せません。',
  },
  'mail.spamset.scope_hint': {
    ko: '차단 정책과 자동 등급은 회사 밖에서 들어온 메일에 적용됩니다. 회사 안에서 보낸 메일은 수신거부목록에 적은 주소만 걸러집니다.',
    en: 'The policy and automatic level apply to mail arriving from outside the company. Mail sent from inside is filtered only by the block list.',
    ja: 'ポリシーと自動レベルは社外から届いたメールに適用されます。社内から送られたメールは受信拒否リストの住所のみ遮断します。',
  },
  'mail.spamset.saved': { ko: '저장됨', en: 'Saved', ja: '保存しました' },
  'mail.spamset.save_failed': { ko: '저장하지 못했습니다', en: 'Could not save', ja: '保存できませんでした' },
  'mail.spamset.legacy': {
    ko: '서버가 아직 이 설정을 모릅니다. 서버를 올린 뒤 다시 시도하세요.',
    en: 'The server does not know this setting yet. Try again after it is updated.',
    ja: 'サーバーがこの設定をまだ知りません。更新後に再試行してください。',
  },
  'mail.spamset.load_failed': { ko: '불러오지 못했습니다', en: 'Could not load', ja: '読み込めませんでした' },

  'mail.spamset.allow': { ko: '수신허용목록', en: 'Allow list', ja: '受信許可リスト' },
  'mail.spamset.block': { ko: '수신거부목록', en: 'Block list', ja: '受信拒否リスト' },
  'mail.spamset.add': { ko: '추가', en: 'Add', ja: '追加' },
  'mail.spamset.clear': { ko: '전체삭제', en: 'Delete all', ja: '全件削除' },
  'mail.spamset.col.address': { ko: '이메일주소', en: 'Email address', ja: 'メールアドレス' },
  'mail.spamset.col.manage': { ko: '삭제', en: 'Delete', ja: '削除' },
  'mail.spamset.delete': { ko: '삭제', en: 'Delete', ja: '削除' },
  'mail.spamset.delete_aria': {
    ko: '{n} 을(를) 목록에서 지우기',
    en: 'Remove {n} from the list',
    ja: '{n} をリストから削除',
  },
  'mail.spamset.from_report': { ko: '스팸 신고로 차단', en: 'Blocked from a spam report', ja: '迷惑メール報告で遮断' },
  'mail.spamset.allow_none': {
    ko: '수신허용목록이 비어 있습니다. 여기에 적은 주소는 차단 정책·자동 판정보다 먼저라, 늘 받은편지함으로 옵니다.',
    en: 'The allow list is empty. Addresses here always reach the inbox — they come before the policy and the automatic check.',
    ja: '受信許可リストは空です。ここに書いた住所はポリシーや自動判定より先で、常に受信箱に届きます。',
  },
  'mail.spamset.block_none': {
    ko: '수신거부목록이 비어 있습니다. 지금은 주소로 막고 있는 사람이 없습니다.',
    en: 'The block list is empty. No address is being blocked right now.',
    ja: '受信拒否リストは空です。今、住所で遮断している相手はいません。',
  },
  'mail.spamset.allow_hint': {
    ko: '여기 있는 주소는 차단 정책·자동 판정보다 먼저입니다 — 늘 받은편지함으로 옵니다.',
    en: 'These addresses come before the policy and the automatic check — they always reach the inbox.',
    ja: 'ここにある住所はポリシーや自動判定より先です — 常に受信箱に届きます。',
  },
  'mail.spamset.block_hint': {
    ko: '여기 있는 주소에서 온 메일은 위에서 고른 처리방법대로 처리됩니다. 회사 안에서 보낸 메일도 여기서는 걸립니다.',
    en: 'Mail from these addresses is handled the way you chose above — including mail sent from inside the company.',
    ja: 'ここにある住所からのメールは上で選んだ処理方法に従います（社内から送られたメールも含みます）。',
  },

  'mail.spamset.add_allow_title': { ko: '수신허용목록에 추가', en: 'Add to the allow list', ja: '受信許可リストに追加' },
  'mail.spamset.add_block_title': { ko: '수신거부목록에 추가', en: 'Add to the block list', ja: '受信拒否リストに追加' },
  'mail.spamset.addr_label': { ko: '이메일주소', en: 'Email address', ja: 'メールアドレス' },
  'mail.spamset.addr_ph': { ko: 'name@example.com', en: 'name@example.com', ja: 'name@example.com' },
  'mail.spamset.register': { ko: '등록', en: 'Add', ja: '登録' },
  'mail.spamset.cancel': { ko: '취소', en: 'Cancel', ja: 'キャンセル' },
  'mail.spamset.err_addr': { ko: '메일주소 형식이 아닙니다', en: 'That is not an email address', ja: 'メールアドレスの形式ではありません' },
  'mail.spamset.err_exists': { ko: '이미 목록에 있는 주소입니다', en: 'That address is already on the list', ja: 'すでにリストにある住所です' },
  'mail.spamset.err_conflict': {
    ko: '반대쪽 목록에 있는 주소입니다. 거기서 먼저 지워 주세요.',
    en: 'That address is on the other list. Remove it there first.',
    ja: '反対側のリストにある住所です。先にそちらから削除してください。',
  },
  'mail.spamset.err_self': {
    ko: '내 주소는 수신거부할 수 없습니다',
    en: 'You cannot block your own address',
    ja: '自分の住所は受信拒否できません',
  },
  'mail.spamset.err_failed': { ko: '등록하지 못했습니다', en: 'Could not add it', ja: '登録できませんでした' },
  'mail.spamset.del_failed': { ko: '지우지 못했습니다', en: 'Could not delete it', ja: '削除できませんでした' },

  'mail.spamset.clear_allow_title': {
    ko: '수신허용목록을 전부 지울까요?',
    en: 'Delete the whole allow list?',
    ja: '受信許可リストをすべて削除しますか？',
  },
  'mail.spamset.clear_block_title': {
    ko: '수신거부목록을 전부 지울까요?',
    en: 'Delete the whole block list?',
    ja: '受信拒否リストをすべて削除しますか？',
  },
  'mail.spamset.clear_allow_desc': {
    ko: '지운 주소는 되돌릴 수 없습니다 — 다시 적어야 합니다.',
    en: 'Deleted addresses cannot be restored — you would have to type them again.',
    ja: '削除した住所は元に戻せません — もう一度入力する必要があります。',
  },
  'mail.spamset.clear_block_desc': {
    ko: '지운 주소는 되돌릴 수 없습니다. 「이 사람 메일도 차단」으로 막아 둔 사람도 함께 풀립니다.',
    en: 'Deleted addresses cannot be restored. Senders you blocked from a spam report are unblocked too.',
    ja: '削除した住所は元に戻せません。「この人のメールも遮断」で止めていた相手も解除されます。',
  },

  /* ── 환경설정 > 자동분류 (migration 161, 2026-09-05) ── */
  'mail.filterset.sec.use': { ko: '자동분류 사용', en: 'Auto-filtering', ja: '自動振り分けの使用' },
  'mail.filterset.sec.rule': { ko: '자동분류 규칙 목록', en: 'Filter rules', ja: '自動振り分けルール一覧' },
  'mail.filterset.enabled': { ko: '자동분류 규칙', en: 'Auto-filtering', ja: '自動振り分け' },
  'mail.filterset.enabled_label': {
    ko: '받은 메일에 규칙을 적용합니다',
    en: 'Apply rules to incoming mail',
    ja: '受信メールにルールを適用します',
  },
  'mail.filterset.order_hint': {
    ko: '위에서부터 보아 처음 맞는 규칙 하나만 적용됩니다. 스팸으로 걸러진 메일에는 적용하지 않습니다.',
    en: 'Only the first matching rule applies, read from the top. Rules do not run on mail filtered as spam.',
    ja: '上から見て最初に一致したルール1つだけが適用されます。迷惑メールと判定されたメールには適用しません。',
  },
  'mail.filterset.off_hint': {
    ko: '지금은 꺼져 있어 규칙이 돌지 않습니다. 규칙을 만들어 두고 켜면 그때부터 적용됩니다.',
    en: 'Filtering is off, so no rule runs. Prepare rules here and turn it on when ready.',
    ja: '今はオフなのでルールは動きません。ルールを用意してからオンにできます。',
  },
  'mail.filterset.none': {
    ko: '만들어 둔 규칙이 없습니다. 「추가」로 첫 규칙을 만들어 보세요.',
    en: 'No rules yet. Use “Add” to create the first one.',
    ja: 'まだルールがありません。「追加」で最初のルールを作れます。',
  },
  'mail.filterset.add': { ko: '추가', en: 'Add', ja: '追加' },
  'mail.filterset.clear': { ko: '전체삭제', en: 'Delete all', ja: '全件削除' },
  'mail.filterset.reorder': { ko: '순서변경', en: 'Reorder', ja: '順序変更' },
  'mail.filterset.reorder_done': { ko: '완료', en: 'Done', ja: '完了' },
  'mail.filterset.col.name': { ko: '분류규칙', en: 'Rule', ja: 'ルール' },
  'mail.filterset.col.cond': { ko: '분류조건', en: 'Conditions', ja: '条件' },
  'mail.filterset.col.action': { ko: '보관메일함/태그', en: 'Folder / tag', ja: 'フォルダ / タグ' },
  'mail.filterset.col.manage': { ko: '관리', en: 'Manage', ja: '管理' },
  'mail.filterset.col.order': { ko: '순서', en: 'Order', ja: '順序' },
  'mail.filterset.edit': { ko: '수정', en: 'Edit', ja: '編集' },
  'mail.filterset.delete': { ko: '삭제', en: 'Delete', ja: '削除' },
  'mail.filterset.up': { ko: '{n} 을(를) 위로', en: 'Move {n} up', ja: '{n} を上へ' },
  'mail.filterset.down': { ko: '{n} 을(를) 아래로', en: 'Move {n} down', ja: '{n} を下へ' },
  'mail.filterset.cond.from': { ko: '보낸사람', en: 'Sender', ja: '差出人' },
  'mail.filterset.cond.to': { ko: '함께받는사람', en: 'Co-recipient', ja: '同時受信者' },
  'mail.filterset.cond.subject': { ko: '제목에 「{v}」 포함', en: 'Subject contains “{v}”', ja: '件名に「{v}」を含む' },
  'mail.filterset.cond.all': { ko: '모두 만족', en: 'Match all', ja: 'すべて満たす' },
  'mail.filterset.cond.any': { ko: '하나만 만족', en: 'Match any', ja: 'いずれか満たす' },
  'mail.filterset.cond.more': { ko: '외 {n}', en: '+{n} more', ja: '他 {n}' },
  'mail.filterset.act.gone': {
    ko: '지워진 메일함 — 옮기지 않습니다',
    en: 'Folder deleted — no longer moves',
    ja: '削除されたフォルダ — 移動しません',
  },
  'mail.filterset.saved': { ko: '저장됨', en: 'Saved', ja: '保存しました' },
  'mail.filterset.save_failed': { ko: '저장하지 못했습니다', en: 'Could not save', ja: '保存できませんでした' },
  'mail.filterset.load_failed': { ko: '불러오지 못했습니다', en: 'Could not load', ja: '読み込めませんでした' },
  'mail.filterset.legacy': {
    ko: '서버가 아직 이 설정을 모릅니다. 서버를 올린 뒤 다시 시도하세요.',
    en: 'The server does not know this setting yet. Try again after it is updated.',
    ja: 'サーバーがこの設定をまだ知りません。更新後に再試行してください。',
  },
  'mail.filterset.clear_title': { ko: '규칙을 전부 지울까요?', en: 'Delete all rules?', ja: 'ルールをすべて削除しますか？' },
  'mail.filterset.clear_desc': {
    ko: '지운 규칙은 되돌릴 수 없습니다. 이미 분류된 메일은 그대로 남습니다.',
    en: 'Deleted rules cannot be restored. Mail already filtered stays where it is.',
    ja: '削除したルールは元に戻せません。すでに振り分けられたメールはそのままです。',
  },
  'mail.filterset.del_title': { ko: '「{n}」 규칙을 지울까요?', en: 'Delete the rule “{n}”?', ja: '「{n}」ルールを削除しますか？' },
  'mail.filterset.del_desc': {
    ko: '지운 규칙은 되돌릴 수 없습니다. 이미 분류된 메일은 그대로 남습니다.',
    en: 'The rule cannot be restored. Mail already filtered stays where it is.',
    ja: 'ルールは元に戻せません。すでに振り分けられたメールはそのままです。',
  },

  /* 자동분류 추가·수정 팝업 */
  'mail.filterset.add_title': { ko: '자동분류 추가', en: 'Add a filter rule', ja: '自動振り分けの追加' },
  'mail.filterset.edit_title': { ko: '자동분류 수정', en: 'Edit the filter rule', ja: '自動振り分けの編集' },
  'mail.filterset.f.name': { ko: '분류규칙 이름', en: 'Rule name', ja: 'ルール名' },
  'mail.filterset.f.name_ph': { ko: '예: 거래처 메일', en: 'e.g. Partner mail', ja: '例: 取引先メール' },
  'mail.filterset.f.address': { ko: '메일주소', en: 'Email address', ja: 'メールアドレス' },
  'mail.filterset.f.address_ph': { ko: '주소를 치고 Enter', en: 'Type an address and press Enter', ja: 'アドレスを入力して Enter' },
  'mail.filterset.f.subject': { ko: '메일제목', en: 'Subject', ja: '件名' },
  'mail.filterset.f.subject_ph': { ko: '포함될 글자', en: 'Text it must contain', ja: '含まれる文字' },
  'mail.filterset.f.subject_suffix': { ko: '포함될 때', en: 'when the subject contains it', ja: '含まれるとき' },
  'mail.filterset.f.match': { ko: '분류조건', en: 'Match', ja: '振り分け条件' },
  'mail.filterset.f.action': { ko: '처리방법', en: 'What to do', ja: '処理方法' },
  'mail.filterset.f.move': { ko: '해당 메일을 메일함으로 이동', en: 'Move the mail to a folder', ja: 'メールをフォルダへ移動' },
  'mail.filterset.f.tag': { ko: '해당 메일에 태그 적용', en: 'Apply a tag to the mail', ja: 'メールにタグを適用' },
  'mail.filterset.f.folder_ph': { ko: '메일함 고르기', en: 'Choose a folder', ja: 'フォルダを選ぶ' },
  'mail.filterset.f.tag_ph': { ko: '태그 고르기', en: 'Choose a tag', ja: 'タグを選ぶ' },
  'mail.filterset.f.no_folder': {
    ko: '만들어 둔 메일함이 없습니다 — 「메일함」 탭에서 먼저 만드세요.',
    en: 'You have no folders yet — create one on the Mailboxes tab first.',
    ja: 'フォルダがありません — 「メールボックス」タブで先に作成してください。',
  },
  'mail.filterset.f.no_tag': {
    ko: '만들어 둔 태그가 없습니다 — 「메일함」 탭에서 먼저 만드세요.',
    en: 'You have no tags yet — create one on the Mailboxes tab first.',
    ja: 'タグがありません — 「メールボックス」タブで先に作成してください。',
  },
  'mail.filterset.save': { ko: '저장', en: 'Save', ja: '保存' },
  'mail.filterset.register': { ko: '등록', en: 'Add', ja: '登録' },
  'mail.filterset.cancel': { ko: '취소', en: 'Cancel', ja: 'キャンセル' },
  'mail.filterset.err_name': { ko: '규칙 이름을 적어 주세요', en: 'Give the rule a name', ja: 'ルール名を入力してください' },
  'mail.filterset.err_cond': {
    ko: '메일주소나 메일제목 중 하나는 있어야 합니다',
    en: 'Give at least one condition — an address or a subject',
    ja: 'メールアドレスか件名のどちらかは必要です',
  },
  'mail.filterset.err_action': {
    ko: '메일함으로 이동하거나 태그를 붙이거나 — 하나는 골라야 합니다',
    en: 'Choose at least one action — move to a folder or apply a tag',
    ja: 'フォルダへ移動かタグ適用 — どちらかは選んでください',
  },
  'mail.filterset.err_addr': { ko: '메일주소 형식이 아닙니다', en: 'That is not an email address', ja: 'メールアドレスの形式ではありません' },
  'mail.filterset.err_dup': { ko: '이미 있습니다', en: 'Already added', ja: 'すでにあります' },
  'mail.filterset.err_failed': { ko: '저장하지 못했습니다', en: 'Could not save it', ja: '保存できませんでした' },
  'mail.filterset.addr_remove': { ko: '{v} 빼기', en: 'Remove {v}', ja: '{v} を削除' },

  /* ── 환경설정 > 자동전달 (migration 162, 2026-09-05) ── */
  'mail.fwd.sec.use': { ko: '자동전달 사용', en: 'Auto-forwarding', ja: '自動転送の使用' },
  'mail.fwd.use_row': { ko: '사용여부', en: 'Use', ja: '使用可否' },
  'mail.fwd.use_label': {
    ko: '받은 메일을 아래 주소로 자동 전달합니다',
    en: 'Automatically forward incoming mail to the addresses below',
    ja: '受信メールを下の住所へ自動転送します',
  },
  'mail.fwd.off_hint': {
    ko: '지금은 꺼져 있어 전달하지 않습니다. 주소를 적어 두고 켜면 그때부터 전달합니다.',
    en: 'Forwarding is off. Add addresses here and turn it on when ready.',
    ja: '今はオフなので転送しません。住所を用意してからオンにできます。',
  },
  'mail.fwd.external_ok': {
    ko: '바깥 주소로는 「외부 메일 계정」의 보내기(SMTP)로 나갑니다.',
    en: 'Mail to outside addresses is sent through your external mail account (SMTP).',
    ja: '社外の住所へは「外部メールアカウント」の送信（SMTP）で送られます。',
  },
  'mail.fwd.external_none': {
    ko: '보내는 계정(SMTP)이 없어 회사 밖 주소로는 전달되지 않습니다. 회사 안 주소로는 그대로 갑니다 — 개인설정 > 외부 메일 계정에서 보내는 서버를 먼저 등록하세요.',
    en: 'No sending account (SMTP), so mail cannot be forwarded outside the company. Addresses inside the company still work — add a sending server in Settings > External mail account.',
    ja: '送信アカウント（SMTP）がないため社外の住所へは転送できません。社内の住所へはそのまま届きます — 個人設定 > 外部メールアカウントで送信サーバーを登録してください。',
  },

  'mail.fwd.sec.addr': { ko: '자동전달 메일주소', en: 'Forwarding addresses', ja: '自動転送メールアドレス' },
  'mail.fwd.addr_none': {
    ko: '전달할 주소가 없습니다. 「추가」로 주소를 넣어 주세요 — 예외 규칙에 걸리지 않은 메일이 여기로 갑니다.',
    en: 'No forwarding address yet. Mail that no exception rule matches goes to these addresses.',
    ja: '転送先の住所がありません。例外ルールに当たらないメールがここへ送られます。',
  },
  'mail.fwd.addr_title': { ko: '자동전달 메일주소 추가', en: 'Add a forwarding address', ja: '自動転送メールアドレスの追加' },

  'mail.fwd.sec.rule': { ko: '예외 자동전달 규칙', en: 'Exception forwarding rules', ja: '例外自動転送ルール' },
  'mail.fwd.rule_none': {
    ko: '예외 규칙이 없습니다. 특정 보낸사람만 다른 주소로 보내고 싶을 때 만듭니다.',
    en: 'No exception rule. Add one when mail from a specific sender should go elsewhere.',
    ja: '例外ルールがありません。特定の差出人だけ別の住所へ送りたいときに作ります。',
  },
  'mail.fwd.rule_hint': {
    ko: '보낸사람이 규칙에 맞으면 위의 기본 주소 대신 그 규칙의 주소로만 전달합니다. 주소로 적은 규칙이 도메인으로 적은 규칙보다 먼저입니다.',
    en: 'When the sender matches a rule, mail goes to that rule’s addresses instead of the default ones. Address rules come before domain rules.',
    ja: '差出人がルールに一致すると、上の既定の住所ではなくそのルールの住所へ送ります。住所のルールがドメインのルールより先です。',
  },
  'mail.fwd.col.match': { ko: '보낸사람 주소(이메일/도메인)', en: 'Sender (address / domain)', ja: '差出人（アドレス／ドメイン）' },
  'mail.fwd.col.targets': { ko: '자동전달 주소 목록', en: 'Forwarding addresses', ja: '自動転送先の一覧' },
  'mail.fwd.col.manage': { ko: '관리', en: 'Manage', ja: '管理' },
  'mail.fwd.col.address': { ko: '이메일주소', en: 'Email address', ja: 'メールアドレス' },
  'mail.fwd.kind.address': { ko: '메일주소', en: 'Address', ja: 'メールアドレス' },
  'mail.fwd.kind.domain': { ko: '도메인', en: 'Domain', ja: 'ドメイン' },

  'mail.fwd.add': { ko: '추가', en: 'Add', ja: '追加' },
  'mail.fwd.clear': { ko: '전체삭제', en: 'Delete all', ja: '全件削除' },
  'mail.fwd.edit': { ko: '수정', en: 'Edit', ja: '編集' },
  'mail.fwd.delete': { ko: '삭제', en: 'Delete', ja: '削除' },
  'mail.fwd.delete_aria': { ko: '{n} 을(를) 목록에서 지우기', en: 'Remove {n}', ja: '{n} をリストから削除' },
  'mail.fwd.select_delete': { ko: '선택삭제', en: 'Delete selected', ja: '選択削除' },

  'mail.fwd.rule_add_title': { ko: '예외 자동전달 규칙 추가', en: 'Add an exception rule', ja: '例外自動転送ルールの追加' },
  'mail.fwd.rule_edit_title': { ko: '예외 자동전달 규칙 수정', en: 'Edit the exception rule', ja: '例外自動転送ルールの編集' },
  'mail.fwd.f.sender': { ko: '보낸사람', en: 'Sender', ja: '差出人' },
  'mail.fwd.f.sender_addr_ph': { ko: 'name@example.com', en: 'name@example.com', ja: 'name@example.com' },
  'mail.fwd.f.sender_dom_ph': { ko: 'example.com', en: 'example.com', ja: 'example.com' },
  'mail.fwd.f.explain': {
    ko: '위 경우와 일치하면 아래의 전달 메일 주소로 모두 자동 전달합니다.',
    en: 'When the sender matches, the mail is forwarded to every address below.',
    ja: '上の条件に一致すると、下の転送先すべてへ自動転送します。',
  },
  'mail.fwd.f.targets': { ko: '전달 메일 주소', en: 'Forwarding addresses', ja: '転送先メールアドレス' },
  'mail.fwd.f.target_ph': { ko: 'name@example.com', en: 'name@example.com', ja: 'name@example.com' },
  'mail.fwd.f.target_none': {
    ko: '아직 없습니다. 주소를 적고 「추가」를 누르세요.',
    en: 'None yet. Type an address and press Add.',
    ja: 'まだありません。住所を入力して「追加」を押してください。',
  },
  'mail.fwd.f.target_count': { ko: '{n} / 20', en: '{n} / 20', ja: '{n} / 20' },
  'mail.fwd.save': { ko: '저장', en: 'Save', ja: '保存' },
  'mail.fwd.register': { ko: '등록', en: 'Add', ja: '登録' },
  'mail.fwd.cancel': { ko: '취소', en: 'Cancel', ja: 'キャンセル' },

  'mail.fwd.saved': { ko: '저장됨', en: 'Saved', ja: '保存しました' },
  'mail.fwd.save_failed': { ko: '저장하지 못했습니다', en: 'Could not save', ja: '保存できませんでした' },
  'mail.fwd.load_failed': { ko: '불러오지 못했습니다', en: 'Could not load', ja: '読み込めませんでした' },
  'mail.fwd.legacy': {
    ko: '서버가 아직 이 설정을 모릅니다. 서버를 올린 뒤 다시 시도하세요.',
    en: 'The server does not know this setting yet. Try again after it is updated.',
    ja: 'サーバーがこの設定をまだ知りません。更新後に再試行してください。',
  },
  'mail.fwd.err_addr': { ko: '메일주소 형식이 아닙니다', en: 'That is not an email address', ja: 'メールアドレスの形式ではありません' },
  'mail.fwd.err_domain': { ko: '도메인 형식이 아닙니다 (예: partner.co.kr)', en: 'That is not a domain (e.g. partner.co.kr)', ja: 'ドメインの形式ではありません（例: partner.co.kr）' },
  'mail.fwd.err_self': { ko: '내 주소로는 전달할 수 없습니다', en: 'You cannot forward to your own address', ja: '自分の住所へは転送できません' },
  'mail.fwd.err_exists': { ko: '이미 목록에 있는 주소입니다', en: 'That address is already on the list', ja: 'すでにリストにある住所です' },
  'mail.fwd.err_rule_exists': { ko: '같은 조건의 규칙이 있습니다', en: 'A rule with that condition already exists', ja: '同じ条件のルールがあります' },
  'mail.fwd.err_targets': {
    ko: '전달할 메일주소를 하나 이상 넣어 주세요',
    en: 'Add at least one forwarding address',
    ja: '転送先メールアドレスを1つ以上入れてください',
  },
  'mail.fwd.err_failed': { ko: '저장하지 못했습니다', en: 'Could not save it', ja: '保存できませんでした' },

  'mail.fwd.clear_addr_title': { ko: '자동전달 주소를 전부 지울까요?', en: 'Delete every forwarding address?', ja: '自動転送の住所をすべて削除しますか？' },
  'mail.fwd.clear_rule_title': { ko: '예외 규칙을 전부 지울까요?', en: 'Delete every exception rule?', ja: '例外ルールをすべて削除しますか？' },
  'mail.fwd.clear_desc': {
    ko: '지운 것은 되돌릴 수 없습니다. 이미 전달된 메일은 그대로입니다.',
    en: 'Deleted entries cannot be restored. Mail already forwarded stays as it is.',
    ja: '削除したものは元に戻せません。すでに転送されたメールはそのままです。',
  },
  'mail.fwd.del_rule_title': { ko: '「{n}」 규칙을 지울까요?', en: 'Delete the rule “{n}”?', ja: '「{n}」ルールを削除しますか？' },

  /* ── 환경설정 > 부재중응답 (migration 163, 2026-09-05) ── */
  'mail.vac.sec.set': { ko: '부재중응답 설정', en: 'Out-of-office', ja: '不在時応答の設定' },
  'mail.vac.use': { ko: '부재중응답', en: 'Out-of-office reply', ja: '不在時応答' },
  'mail.vac.use_label': { ko: '받은 메일에 부재중응답을 보냅니다', en: 'Send an out-of-office reply to incoming mail', ja: '受信メールに不在時応答を送ります' },
  'mail.vac.off_hint': {
    ko: '적용하지 않음. 부재기간과 메시지를 적은 뒤 켜면 그때부터 보냅니다.',
    en: 'Not applied. Fill in the period and the message, then turn it on.',
    ja: '適用しません。不在期間とメッセージを入れてからオンにできます。',
  },
  'mail.vac.once_hint': {
    ko: '한 사람에게는 부재기간 동안 한 번만 답합니다. 자동으로 온 메일(no-reply · 자동전달)에는 답하지 않습니다.',
    en: 'Each sender gets one reply per absence. No reply is sent to automated mail (no-reply, forwards).',
    ja: '同じ差出人には不在期間中1回だけ返します。自動送信メール（no-reply・自動転送）には返しません。',
  },
  'mail.vac.external_none': {
    ko: '보내는 계정(SMTP)이 없어 회사 밖 사람에게는 답하지 못합니다. 회사 안 사람에게는 그대로 갑니다.',
    en: 'No sending account (SMTP), so people outside the company get no reply. People inside still do.',
    ja: '送信アカウント（SMTP）がないため社外の相手には返せません。社内の相手には届きます。',
  },
  'mail.vac.period': { ko: '부재기간 설정', en: 'Absence period', ja: '不在期間の設定' },
  'mail.vac.period_ph': { ko: '시작일 ~ 종료일', en: 'Start ~ end', ja: '開始日 ~ 終了日' },
  'mail.vac.subject': { ko: '부재중응답 제목', en: 'Reply subject', ja: '不在時応答の件名' },
  'mail.vac.subject_ph': { ko: '예: 휴가 중입니다', en: 'e.g. Out of office', ja: '例: 休暇中です' },
  'mail.vac.subject_hint': {
    ko: '비워 두면 받은 메일 제목 앞에 「Re:」를 붙여 보냅니다.',
    en: 'Left empty, the original subject is reused with “Re:”.',
    ja: '空にすると受信メールの件名に「Re:」を付けて送ります。',
  },
  'mail.vac.body': { ko: '부재중응답 메시지', en: 'Reply message', ja: '不在時応答メッセージ' },
  'mail.vac.body_ph': {
    ko: '예: 9월 10일까지 자리를 비웁니다. 급한 일은 담당자에게 연락해 주세요.',
    en: 'e.g. I am away until 10 September. For anything urgent, please contact…',
    ja: '例: 9月10日まで不在です。お急ぎの場合は担当者へご連絡ください。',
  },
  'mail.vac.scope': { ko: '부재중응답 대상', en: 'Who gets a reply', ja: '不在時応答の対象' },
  'mail.vac.scope.all': { ko: '수신된 모든 메일에 부재중 응답', en: 'Reply to all incoming mail', ja: '受信したすべてのメールに応答' },
  'mail.vac.scope.listed': { ko: '저장한 응답 대상에게만 부재중 응답', en: 'Reply only to saved addresses', ja: '保存した対象にのみ応答' },

  'mail.vac.sec.target': { ko: '응답 대상', en: 'Reply targets', ja: '応答対象' },
  'mail.vac.add': { ko: '추가', en: 'Add', ja: '追加' },
  'mail.vac.clear': { ko: '전체삭제', en: 'Delete all', ja: '全件削除' },
  'mail.vac.delete': { ko: '삭제', en: 'Delete', ja: '削除' },
  'mail.vac.col.address': { ko: '이메일주소', en: 'Email address', ja: 'メールアドレス' },
  'mail.vac.col.manage': { ko: '관리', en: 'Manage', ja: '管理' },
  'mail.vac.delete_aria': { ko: '{n} 을(를) 목록에서 지우기', en: 'Remove {n}', ja: '{n} をリストから削除' },
  'mail.vac.target_title': { ko: '응답대상 추가', en: 'Add a reply target', ja: '応答対象の追加' },
  'mail.vac.target_none': {
    ko: '응답 대상이 없습니다. 지금 켜면 아무에게도 답하지 않습니다 — 「추가」로 주소를 넣어 주세요.',
    en: 'No target yet. With this setting nobody gets a reply — add an address.',
    ja: '対象がありません。今のままでは誰にも返しません — 住所を追加してください。',
  },
  'mail.vac.clear_title': { ko: '응답 대상을 전부 지울까요?', en: 'Delete every reply target?', ja: '応答対象をすべて削除しますか？' },
  'mail.vac.clear_desc': {
    ko: '지운 주소는 되돌릴 수 없습니다.',
    en: 'Deleted addresses cannot be restored.',
    ja: '削除した住所は元に戻せません。',
  },
  'mail.vac.del_title': { ko: '「{n}」 을(를) 지울까요?', en: 'Delete “{n}”?', ja: '「{n}」を削除しますか？' },

  'mail.vac.save': { ko: '저장', en: 'Save', ja: '保存' },
  'mail.vac.saved': { ko: '저장됨', en: 'Saved', ja: '保存しました' },
  'mail.vac.save_failed': { ko: '저장하지 못했습니다', en: 'Could not save', ja: '保存できませんでした' },
  'mail.vac.load_failed': { ko: '불러오지 못했습니다', en: 'Could not load', ja: '読み込めませんでした' },
  'mail.vac.legacy': {
    ko: '서버가 아직 이 설정을 모릅니다. 서버를 올린 뒤 다시 시도하세요.',
    en: 'The server does not know this setting yet. Try again after it is updated.',
    ja: 'サーバーがこの設定をまだ知りません。更新後に再試行してください。',
  },
  'mail.vac.err_period': { ko: '부재기간을 먼저 정해 주세요', en: 'Set the absence period first', ja: '不在期間を先に決めてください' },
  'mail.vac.err_bad_period': { ko: '시작일이 종료일보다 뒤일 수 없습니다', en: 'The start date cannot be after the end date', ja: '開始日が終了日より後にはできません' },
  'mail.vac.err_body': { ko: '부재중응답 메시지를 적어 주세요', en: 'Write the reply message', ja: '不在時応答メッセージを入力してください' },
  'mail.vac.err_addr': { ko: '메일주소 형식이 아닙니다', en: 'That is not an email address', ja: 'メールアドレスの形式ではありません' },
  'mail.vac.err_exists': { ko: '이미 목록에 있는 주소입니다', en: 'That address is already on the list', ja: 'すでにリストにある住所です' },
  'mail.vac.err_failed': { ko: '저장하지 못했습니다', en: 'Could not save it', ja: '保存できませんでした' },

  /* ── 환경설정 > 외부메일 (migration 164, 2026-09-05) ── */
  'mail.ext.sec.list': { ko: '외부메일 목록', en: 'External mail accounts', ja: '外部メール一覧' },
  'mail.ext.add': { ko: '추가', en: 'Add', ja: '追加' },
  'mail.ext.clear': { ko: '전체삭제', en: 'Delete all', ja: '全件削除' },
  'mail.ext.edit': { ko: '수정', en: 'Edit', ja: '編集' },
  'mail.ext.delete': { ko: '삭제', en: 'Delete', ja: '削除' },
  'mail.ext.none': {
    ko: '등록한 외부메일 계정이 없습니다. 「추가」로 다른 메일함의 메일을 여기로 가져올 수 있습니다.',
    en: 'No external account yet. Use “Add” to bring mail from another mailbox here.',
    ja: '登録した外部メールアカウントがありません。「追加」で他のメールボックスのメールを取り込めます。',
  },
  'mail.ext.col.account': { ko: '메일주소', en: 'Address', ja: 'メールアドレス' },
  'mail.ext.col.recv': { ko: '받기방식', en: 'Protocol', ja: '受信方式' },
  'mail.ext.col.port': { ko: '포트', en: 'Port', ja: 'ポート' },
  'mail.ext.col.secure': { ko: '보안연결(SSL)', en: 'SSL', ja: '安全な接続(SSL)' },
  'mail.ext.col.user': { ko: '아이디', en: 'User', ja: 'ID' },
  'mail.ext.col.folder': { ko: '저장메일함', en: 'Save to', ja: '保存フォルダ' },
  'mail.ext.col.delorig': { ko: '원본삭제여부', en: 'Delete original', ja: '原本削除' },
  'mail.ext.col.manage': { ko: '관리', en: 'Manage', ja: '管理' },
  'mail.ext.yes': { ko: '사용', en: 'Yes', ja: 'あり' },
  'mail.ext.no': { ko: '사용 안 함', en: 'No', ja: 'なし' },
  'mail.ext.inbox': { ko: '받은편지함', en: 'Inbox', ja: '受信箱' },
  'mail.ext.folder_gone': { ko: '지워진 메일함 — 받은편지함으로', en: 'Folder deleted — inbox', ja: '削除されたフォルダ — 受信箱へ' },
  'mail.compose.label_from': { ko: '보내는 계정', en: 'From account', ja: '送信アカウント' },
  'mail.compose.from_unusable': {
    ko: '{from} 로는 보낼 수 없어 {fallback} 에서 나갑니다 — 그 계정에 보내는 서버(SMTP)를 등록하세요.',
    en: 'Cannot send from {from}; it will go out from {fallback} — register an SMTP server for that account.',
    ja: '{from} からは送信できないため {fallback} から送信されます — そのアカウントにSMTPサーバーを登録してください。',
  },
  'mail.ext.f.same_pass': { ko: '받기 비밀번호와 같음', en: 'Same as receiving password', ja: '受信パスワードと同じ' },
  'mail.ext.f.same_pass_hint': {
    ko: '저장할 때 받기 비밀번호를 그대로 씁니다 — 나중에 받기 비밀번호를 바꾸면 여기도 다시 저장해 주세요.',
    en: 'The receiving password is copied when you save — if you change it later, save this again.',
    ja: '保存時に受信パスワードをそのまま使います — 後で受信パスワードを変えたら、ここも保存し直してください。',
  },
  'mail.ext.f.sec.folders': { ko: '가져올 폴더', en: 'Folders to import', ja: '取り込むフォルダ' },
  'mail.ext.f.folders_load': { ko: '폴더 불러오기', en: 'Load folders', ja: 'フォルダを読み込む' },
  'mail.ext.f.folders_loading': { ko: '불러오는 중…', en: 'Loading…', ja: '読み込み中…' },
  'mail.ext.f.folders_after_save': {
    ko: '계정을 저장한 뒤에 고를 수 있습니다 — 폴더 목록은 서버에 붙어 봐야 알 수 있습니다.',
    en: 'Available after you save the account — the folder list comes from the server.',
    ja: 'アカウントを保存した後に選べます — フォルダ一覧はサーバーに接続して取得します。',
  },
  'mail.ext.f.folders_hint': {
    ko: '아무것도 안 고르면 받은편지함만 가져옵니다. 고른 폴더는 같은 이름의 메일함에 담깁니다.',
    en: 'With none selected, only the inbox is imported. Selected folders go to a mailbox of the same name.',
    ja: '何も選ばない場合は受信箱のみ取り込みます。選んだフォルダは同じ名前のメールボックスに入ります。',
  },
  'mail.ext.f.folders_not_advised': { ko: '권하지 않음', en: 'not advised', ja: '非推奨' },
  'mail.ext.f.push_seen': {
    ko: '여기서 읽으면 서버에도 읽음으로',
    en: 'Mark read on the server too',
    ja: 'ここで読んだらサーバーにも既読を付ける',
  },
  'mail.ext.f.push_seen_hint': {
    ko: '끄면 서버는 그대로 안 읽음으로 남습니다 — 휴대폰·웹메일에서 다시 읽은 표시를 해야 합니다.',
    en: 'When off, the server stays unread — you would mark it read again on your phone or webmail.',
    ja: 'オフの場合サーバーは未読のままです — スマホやWebメールで再度既読にする必要があります。',
  },
  'mail.ext.col.lastsync': { ko: '마지막 가져오기', en: 'Last import', ja: '最後の取り込み' },
  'mail.ext.sync_never': { ko: '아직 없음', en: 'not yet', ja: 'まだなし' },
  'mail.ext.sync_failed': { ko: '실패', en: 'failed', ja: '失敗' },
  'mail.ext.f.interval': { ko: '확인 주기', en: 'Check every', ja: '確認周期' },
  'mail.ext.iv.1m': { ko: '1분마다', en: 'every 1 min', ja: '1分ごと' },
  'mail.ext.iv.3m': { ko: '3분마다', en: 'every 3 min', ja: '3分ごと' },
  'mail.ext.iv.5m': { ko: '5분마다', en: 'every 5 min', ja: '5分ごと' },
  'mail.ext.iv.15m': { ko: '15분마다', en: 'every 15 min', ja: '15分ごと' },
  'mail.ext.iv.30m': { ko: '30분마다', en: 'every 30 min', ja: '30分ごと' },
  'mail.ext.iv.1h': { ko: '1시간마다', en: 'every hour', ja: '1時間ごと' },
  'mail.ext.moved_note': {
    ko: '개인설정에 있던 「외부 메일 계정」을 이 탭으로 옮겼습니다.',
    en: 'The external mail account screen moved here from personal settings.',
    ja: '個人設定にあった「外部メールアカウント」をこのタブへ移しました。',
  },

  'mail.ext.add_title': { ko: '외부메일 계정 추가', en: 'Add an external account', ja: '外部メールアカウントの追加' },
  'mail.ext.edit_title': { ko: '외부메일 계정 수정', en: 'Edit the external account', ja: '外部メールアカウントの編集' },
  'mail.ext.f.email': { ko: '메일주소', en: 'Email address', ja: 'メールアドレス' },
  'mail.ext.f.recv': { ko: '받기방식', en: 'Receiving protocol', ja: '受信方式' },
  'mail.ext.f.host': { ko: '받는 서버', en: 'Incoming server', ja: '受信サーバー' },
  'mail.ext.f.port': { ko: '포트', en: 'Port', ja: 'ポート' },
  'mail.ext.f.secure': { ko: '보안연결(SSL)', en: 'Secure connection (SSL)', ja: '安全な接続(SSL)' },
  'mail.ext.f.user': { ko: '아이디', en: 'User', ja: 'ID' },
  'mail.ext.f.pass': { ko: '비밀번호', en: 'Password', ja: 'パスワード' },
  'mail.ext.f.pass_keep': { ko: '비워 두면 지금 것을 그대로 씁니다', en: 'Leave empty to keep the saved one', ja: '空にすると今のものをそのまま使います' },
  'mail.ext.f.smtp': { ko: '보내는 서버(SMTP)', en: 'Outgoing server (SMTP)', ja: '送信サーバー(SMTP)' },
  'mail.ext.f.folder': { ko: '저장메일함', en: 'Save to', ja: '保存フォルダ' },
  'mail.ext.f.folder_hint': {
    ko: '자동분류 규칙에 걸린 메일은 그 규칙의 메일함으로 갑니다.',
    en: 'Mail matched by a filter rule goes to that rule’s folder instead.',
    ja: '自動振り分けルールに当たったメールはそのルールのフォルダへ行きます。',
  },
  'mail.ext.f.delorig': { ko: '원본삭제여부', en: 'Delete original', ja: '原本削除' },
  'mail.ext.f.delorig_label': { ko: '가져온 뒤 원본 서버에서 지웁니다', en: 'Delete from the original server after fetching', ja: '取り込んだ後、元のサーバーから削除します' },
  'mail.ext.f.delorig_warn': {
    ko: '지우면 휴대폰이나 다른 프로그램에서도 사라집니다 — 되돌릴 수 없습니다.',
    en: 'Deleted mail also disappears from your phone and other apps — this cannot be undone.',
    ja: '削除するとスマートフォンや他のアプリからも消えます — 元に戻せません。',
  },
  'mail.ext.f.sec.recv': { ko: '받기', en: 'Receiving', ja: '受信' },
  'mail.ext.f.sec.send': { ko: '보내기', en: 'Sending', ja: '送信' },
  'mail.ext.f.sec.store': { ko: '저장', en: 'Storing', ja: '保存' },
  'mail.ext.save': { ko: '저장', en: 'Save', ja: '保存' },
  'mail.ext.register': { ko: '등록', en: 'Add', ja: '登録' },
  'mail.ext.probe': { ko: '연결확인', en: 'Test connection', ja: '接続確認' },
  'mail.ext.probe_recv': { ko: '받기', en: 'Receive', ja: '受信' },
  'mail.ext.probe_smtp': { ko: '보내기', en: 'Send', ja: '送信' },
  'mail.ext.probe_ok': { ko: '연결됨', en: 'Connected', ja: '接続しました' },
  'mail.ext.probe_skip': { ko: '설정 없음', en: 'Not set', ja: '未設定' },
  'mail.ext.probe_failed': { ko: '연결 확인을 하지 못했습니다', en: 'Could not test the connection', ja: '接続確認ができませんでした' },
  'mail.ext.cancel': { ko: '취소', en: 'Cancel', ja: 'キャンセル' },
  'mail.ext.err_email': { ko: '메일주소 형식이 아닙니다', en: 'That is not an email address', ja: 'メールアドレスの形式ではありません' },
  'mail.ext.err_host': { ko: '받는 서버와 아이디를 적어 주세요', en: 'Fill in the incoming server and user', ja: '受信サーバーとIDを入力してください' },
  'mail.ext.err_pass': { ko: '비밀번호를 적어 주세요', en: 'Enter the password', ja: 'パスワードを入力してください' },
  'mail.ext.err_exists': { ko: '이미 등록된 주소입니다', en: 'That address is already registered', ja: 'すでに登録された住所です' },
  'mail.ext.err_failed': { ko: '저장하지 못했습니다', en: 'Could not save it', ja: '保存できませんでした' },
  'mail.ext.load_failed': { ko: '불러오지 못했습니다', en: 'Could not load', ja: '読み込めませんでした' },
  'mail.ext.saved': { ko: '저장됨', en: 'Saved', ja: '保存しました' },
  'mail.ext.probe_bad': { ko: '저장했지만 연결 확인에 실패했습니다 — {m}', en: 'Saved, but the connection check failed — {m}', ja: '保存しましたが接続確認に失敗しました — {m}' },
  'mail.ext.del_title': { ko: '「{n}」 계정을 지울까요?', en: 'Delete the account “{n}”?', ja: '「{n}」アカウントを削除しますか？' },
  'mail.ext.del_desc': {
    ko: '계정 설정만 지웁니다. 이미 가져온 메일은 그대로 남습니다.',
    en: 'Only the account settings are removed. Mail already fetched stays.',
    ja: 'アカウント設定のみ削除します。すでに取り込んだメールは残ります。',
  },
  'mail.ext.clear_title': { ko: '외부메일 계정을 전부 지울까요?', en: 'Delete every external account?', ja: '外部メールアカウントをすべて削除しますか？' },

  /* ── 기본환경으로 옮겨 온 넷 (migration 110 · 2026-09-06 사용자 지시) ── */
  'mail.settings.first_folder': { ko: '첫 화면 메일함', en: 'Opening mailbox', ja: '最初に開くメールボックス' },
  'mail.settings.page_size': { ko: '목록 한 쪽 줄 수', en: 'Rows per page', ja: '1ページの行数' },
  'mail.settings.page_size_hint': {
    ko: '한 쪽에 몇 통씩 볼지 정합니다. 목록 아래 쪽 번호가 이 값으로 나뉩니다.',
    en: 'How many messages one page holds — the pager below the list follows this.',
    ja: '1ページに何通表示するかです。一覧下のページ番号がこの値で分かれます。',
  },
  'mail.settings.simplify': { ko: '제목 단순화', en: 'Simplify subjects', ja: '件名の簡略化' },
  'mail.settings.simplify_label': {
    ko: '제목 앞의 답장 · 전달 표시(RE: · FW:)를 접어서 보여 줍니다',
    en: 'Fold the RE: / FW: prefixes in subjects',
    ja: '件名の先頭の RE: ・ FW: をたたんで表示します',
  },
  'mail.settings.quote': { ko: '답장 인용 구분선', en: 'Reply quote divider', ja: '返信の引用区切り線' },
  'mail.settings.quote_label': {
    ko: '답장·전달 본문 앞에 원본 구분선을 넣습니다',
    en: 'Insert a divider before the quoted original in replies and forwards',
    ja: '返信・転送の本文の前に元メールの区切り線を入れます',
  },
  'mail.settings.moved_in': {
    ko: '개인설정 > 메일에 있던 항목을 이 탭으로 옮겼습니다.',
    en: 'The mail options from personal settings moved into this tab.',
    ja: '個人設定 > メールにあった項目をこのタブへ移しました。',
  },

  /* ── 환경설정 > 최근보낸메일 (migration 165, 2026-09-06) ── */
  'mail.recent.sec': { ko: '최근보낸메일 주소', en: 'Recently sent to', ja: '最近送信した宛先' },
  'mail.recent.clear': { ko: '전체삭제', en: 'Delete all', ja: '全件削除' },
  'mail.recent.delete': { ko: '삭제', en: 'Delete', ja: '削除' },
  'mail.recent.delete_aria': { ko: '{n} 을(를) 목록에서 지우기', en: 'Remove {n}', ja: '{n} をリストから削除' },
  'mail.recent.col.address': { ko: '이메일주소', en: 'Email address', ja: 'メールアドレス' },
  'mail.recent.col.uses': { ko: '보낸 횟수', en: 'Sent', ja: '送信回数' },
  'mail.recent.col.last': { ko: '마지막으로 보낸 날', en: 'Last sent', ja: '最後に送った日' },
  'mail.recent.col.manage': { ko: '관리', en: 'Manage', ja: '管理' },
  'mail.recent.none': {
    ko: '최근에 보낸 메일이 없습니다. 메일을 보내면 그 주소가 여기 쌓이고, 쓰기 화면에서 자동으로 채워집니다.',
    en: 'Nothing sent yet. Addresses you send to appear here and are suggested while composing.',
    ja: '最近送ったメールがありません。送るとその宛先がここに積まれ、作成画面で補完されます。',
  },
  'mail.recent.hint': {
    ko: '여기서 지운 주소는 쓰기 화면의 받는사람 자동완성에서도 빠집니다. 보낸편지함의 메일은 그대로입니다.',
    en: 'An address removed here is also dropped from recipient auto-complete. Mail in Sent stays as it is.',
    ja: 'ここで消した宛先は作成画面の宛先補完からも外れます。送信済みのメールはそのままです。',
  },
  'mail.recent.del_title': { ko: '「{n}」 을(를) 목록에서 지울까요?', en: 'Remove “{n}” from the list?', ja: '「{n}」をリストから消しますか？' },
  'mail.recent.clear_title': { ko: '최근보낸메일 주소를 전부 지울까요?', en: 'Remove every recent address?', ja: '最近送信した宛先をすべて消しますか？' },
  'mail.recent.del_desc': {
    ko: '목록과 자동완성에서만 사라집니다. 보낸 메일 자체는 지워지지 않고, 다시 보내면 또 나타납니다.',
    en: 'It disappears from the list and auto-complete only. The sent mail itself stays, and sending again brings it back.',
    ja: 'リストと補完から消えるだけです。送信済みメールは消えず、また送れば再び現れます。',
  },
  'mail.recent.saved': { ko: '지웠습니다', en: 'Removed', ja: '削除しました' },
  'mail.recent.failed': { ko: '지우지 못했습니다', en: 'Could not remove it', ja: '削除できませんでした' },
  'mail.recent.load_failed': { ko: '불러오지 못했습니다', en: 'Could not load', ja: '読み込めませんでした' },
  'mail.recent.never': { ko: '—', en: '—', ja: '—' },

  'mail.split.title': {
    ko: '끌어서 크기 조절 · 두 번 누르면 기본값',
    en: 'Drag to resize · double-click to reset',
    ja: 'ドラッグでサイズ変更・ダブルクリックで既定に戻す',
  },
} satisfies Record<string, Record<Lang, string>>;

export type MailI18nKey = keyof typeof MESSAGES;

export function tMail(key: MailI18nKey): string {
  const m = MESSAGES[key];
  if (!m) return key;
  return m[getSharedLang()] ?? m.en ?? m[FALLBACK_LANG] ?? key;
}

export function getMailLang(): Lang { return getSharedLang(); }
export function setMailLang(value: Lang): void { setSharedLang(value); }
export function subscribeMailLang(fn: () => void): () => void {
  return subscribeSharedLang(fn);
}
